import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BRANCHES, BRANCH_HIDDEN, calcFourPillars, STEMS, STEM_ELEMENT, type Branch, type Element, type FourPillars, type Stem } from '@/utils/baziCalc';
import { buildShenshaItemByName, calcShensha, getShenshaLevelLabel, type ShenshaItem } from '@/utils/calcTools/bazi/shenshaEngine';
import { ELEMENT_COLORS, getShishen } from '@/utils/calcTools/bazi/shishenEngine';
import {
  buildWuxingDetail,
  buildWuxingVisibleCounts,
  buildWuxingHiddenCounts,
  getWuxingOverview,
  getWuxingSeasonStates,
  ELEMENT_ICONS,
} from '@/utils/calcTools/bazi/wuxingEngine';
import { BRANCH_FIRST_WUXING } from '../jieQiData';
import { jieQiList } from '../mockData';
import {
  convertText,
  getStemLabel,
  getBranchLabel,
  getElementLabel,
  getStrengthLevelLabel,
  getWuxingRelDesc,
  getWuxingFixedLocale,
  getWuxingAnalysisLocale,
} from '@/utils/langConvert';
import { calcXiaoyun, type XiaoyunItem } from '@/utils/calcTools/bazi/xiaoyunEngine';
import { calcYueling, type YuelingResult } from '@/utils/calcTools/bazi/yuelingEngine';
import { resolveQiyunCollapsedLabel } from '../qiyunCollapsed';
import { computeFlowCalendar, type FlowCalendarResult, type FlowDay, type FlowHour, type FlowMonth } from '@/utils/calcTools/bazi/flowCalendarEngine';
import JieQiModal from './JieQiModal';
import BaziInsightModal from './BaziInsightModal';
import type { ServerInsightData } from '@/pages/experience/serverInsight';
import { DaYun, LiuNian, Pillar } from '../types';
import { getProfessionalGridSurface } from './professionalPaipanUi';

interface MingGongData {
  tianGan: string;
  diZhi: string;
  naYin: string;
  tianGanColor?: string;
  diZhiColor?: string;
}

export interface ProfessionalPaipanProps {
  pillars: Pillar[];
  daYun: DaYun[];
  liuNian: LiuNian[];
  qiYunInfo: string;
  jiaoYunInfo: string;
  currentAge: number;
  siLing: string;
  mingGong?: MingGongData;
  isDark?: boolean;
  birthTime?: string;
  birthPlace?: string;
  gender?: string;
  currentYear?: number;
  startAge?: number;
  startMonths?: number;
  isForward?: boolean;
  preciseJiaoYear?: number;
  preciseJiaoMonth?: number;
  fourPillars?: FourPillars;
  shenshaItems?: ShenshaItem[];
  tableShenshaItems?: ShenshaItem[];
  shenshaDisplay?: 'all' | 'common' | 'none';
  yuelingData?: YuelingResult | null;
  xiaoyunItems?: XiaoyunItem[];
  geju?: ServerInsightData['geju'];
  xiyong?: ServerInsightData['xiyong'];
  rizhu?: ServerInsightData['rizhu'];
}

type DayunYearRange = Pick<DaYun, 'startYear' | 'endYear'> | { startYear?: number; endYear?: number };

/** 服务端精确十神优先（先 tianGanShiShen/diZhiMainShiShen，缺失回退 tianGanShen/diZhiShen）。 */
export function resolveTransitTenGods(item: {
  tianGanShen?: string;
  diZhiShen?: string;
  tianGanShiShen?: string;
  diZhiMainShiShen?: string;
}) {
  return {
    stem: item.tianGanShiShen !== undefined ? item.tianGanShiShen : item.tianGanShen,
    branch: item.diZhiMainShiShen !== undefined ? item.diZhiMainShiShen : item.diZhiShen,
  };
}

/** 地支主气十神（服务端精确优先）+ 独立五行（本地 BRANCH_FIRST_WUXING，与服务端同表）。 */
export function resolveTransitBranchDisplay(item: {
  diZhi: string;
  diZhiShen?: string;
  diZhiMainShiShen?: string;
}) {
  return {
    shishen: item.diZhiMainShiShen !== undefined ? item.diZhiMainShiShen : item.diZhiShen,
    wuxing: BRANCH_FIRST_WUXING[item.diZhi]?.wuxing || '',
  };
}

/** 过滤出落在某步大运起止年份范围内的流年行，保持原顺序。 */
function getScopedLiunianList<T extends { year?: number }>(
  liunian: T[],
  dayun?: { startYear?: number; endYear?: number },
): T[] {
  if (!dayun || dayun.startYear === undefined || dayun.endYear === undefined) return liunian;
  return liunian.filter(item => item.year !== undefined && item.year >= dayun.startYear && item.year <= dayun.endYear);
}

/** 某步大运内的年份号列表。 */
export function getDayunScopedLiunianYears(dayun: DayunYearRange, liunian: LiuNian[]): number[] {
  return getScopedLiunianList(liunian, dayun).map(item => item.year).filter((year): year is number => year !== undefined);
}

/** 默认选中流年：当年在作用域内取当年，否则取作用域首个；作用域为空回退当前年。 */
export function resolveDefaultLiunianYear(scopedYears: number[], currentYear: number): number {
  if (scopedYears.includes(currentYear)) return currentYear;
  return scopedYears[0] ?? currentYear;
}

/** 首次渲染默认选中流年 = 当前 active 大运作用域内（当年优先，否则首年）。 */
export function resolveInitialTransitYear(dayun: DaYun[], liunian: LiuNian[], currentYear: number): number {
  const defaultIndex = Math.max(0, dayun.findIndex(item => item.isActive));
  const activeDayun = dayun[defaultIndex];
  return resolveDefaultLiunianYear(
    activeDayun ? getDayunScopedLiunianYears(activeDayun, liunian) : liunian.map(item => item.year),
    currentYear,
  );
}

/** 点某步大运后：选中该大运并把流年夹回该运内（当年优先，否则首年）。 */
export function resolveDayunClickSelection(
  dayun: DaYun[],
  liunian: LiuNian[],
  index: number,
  currentYear: number,
): { selectedDayunIndex: number; selectedLiunianYear: number } {
  const dy = dayun[index];
  const scopedYears = dy ? getDayunScopedLiunianYears(dy, liunian) : [];
  return {
    selectedDayunIndex: index,
    selectedLiunianYear: resolveDefaultLiunianYear(scopedYears, currentYear),
  };
}

/** 把 'yyyy-MM-dd HH:mm'（或含 T）解析为 Date；空串/非法输入返回 null。 */
function parseFlowDate(value: string): Date | null {
  if (!value) return null;
  const t = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(t);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** 返回 termStart <= now < termEnd 命中的节令月下标；无命中返回 -1。 */
export function resolveFlowMonthIndex(months: Array<Pick<FlowMonth, 'termStart' | 'termEnd'>>, now: Date): number {
  for (let i = 0; i < months.length; i += 1) {
    const start = parseFlowDate(months[i].termStart);
    const end = parseFlowDate(months[i].termEnd);
    if (start && end && now >= start && now < end) return i;
  }
  return -1;
}

/** 流日条应高亮的下标：所选流年为「当年」且 days 命中「今天」→ 该下标；非当年/未命中 → 0。 */
export function resolveFlowDayIndex(days: Array<Pick<FlowDay, 'date'>>, now: Date, year: number): number {
  if (year !== now.getFullYear()) return 0;
  const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const idx = days.findIndex(item => item.date === key);
  return idx >= 0 ? idx : 0;
}

/** 流历节点起止时刻 'yyyy-MM-dd HH:mm' → 仅取 'M/D' 展示。 */
export function formatFlowTermDate(termStart: string): string {
  const date = parseFlowDate(termStart);
  if (!date) return '';
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/** 服务端空神煞数组表示「明确无神煞」；undefined 才回退本地计算。 */
export function resolveTransitShenshaItems(
  serverShenSha: string[] | undefined,
  localItems: ShenshaItem[],
): ShenshaItem[] {
  return serverShenSha === undefined ? localItems : namesToShenshaItems(serverShenSha);
}

/** 服务端返回的大运/流年神煞名数组 → 展示用 ShenshaItem（引擎规则补全 desc/等级/颜色/影响力） */
function namesToShenshaItems(names: string[]): ShenshaItem[] {
  return names.map(name => buildShenshaItemByName(name, 'zh-CN'));
}

/**
 * 把服务端 qiYunInfo（「10年8个月20天14小时28分后起运」）转为
 * 「出生后 10年8个月20天14小时28分 起运」展示文案。
 * 无「后起运」后缀的输入（如本地 fallback 文案）原样返回，避免双重包装。
 * 空值返回空串。
 */
export function formatQiYunOffset(qiYunInfo: string): string {
  if (!qiYunInfo) return '';
  const hasSuffix = /后起运$/.test(qiYunInfo);
  if (!hasSuffix) return qiYunInfo;
  const body = qiYunInfo.replace(/后起运$/, '');
  return `出生后 ${body} 起运`;
}

export function calcYinYangRef(fourPillars?: FourPillars, gender?: string): string {
  if (!fourPillars) return '平衡';
  const YANG_STEMS = new Set(['甲', '丙', '戊', '庚', '壬']);
  const YANG_BRANCHES = new Set(['子', '寅', '辰', '午', '申', '戌']);
  const yangCount = [fourPillars.year, fourPillars.month, fourPillars.day, fourPillars.hour].reduce((count, p) => (
    count + (YANG_STEMS.has(p.stem) ? 1 : 0) + (YANG_BRANCHES.has(p.branch) ? 1 : 0)
  ), 0);
  const ratio = yangCount / 8;
  const isFemale = gender?.includes('女') || gender === 'female';
  if (yangCount >= 7) return '阳盛';
  if (yangCount === 6) return isFemale ? '阳盛' : '偏阳';
  if (ratio >= 0.58) return '偏阳';
  if (ratio <= 0.25) return '阴盛';
  if (ratio <= 0.42) return '偏阴';
  return '平衡';
}

type Props = ProfessionalPaipanProps;

type TransitKind = '大运' | '流年';

interface TransitShensha {
  label: string;
  year?: number;
  startYear?: number;
  endYear?: number;
  stem: string;
  branch: string;
  stemShishen?: string;
  branchShishen?: string;
  tianGanShiShen?: string;
  diZhiMainShiShen?: string;
  isActive?: boolean;
  items: ShenshaItem[];
}

interface SelectedShensha {
  item: ShenshaItem;
  context: string;
}

const LEVEL_WEIGHT: Record<string, number> = { 大吉: 5, 吉: 4, 中: 3, 注意: 2, 化解: 1 };
const PILLAR_KEYS = ['年柱', '月柱', '日柱', '时柱'] as const;
const JIEQI_BRANCH_MAP: Record<number, string> = {
  0:'寅',1:'卯',2:'辰',3:'巳',4:'午',5:'未',6:'申',7:'酉',8:'戌',9:'亥',10:'子',11:'丑',
};
const TRANSIT_PILLAR_LABELS = new Set(['时柱', '時柱', 'Hour', '시주']);
const CHANG_SHENG_NAMES = ['长生','沐浴','冠带','临官','帝旺','衰','病','死','墓','绝','胎','养'];
const CHANG_SHENG_MAP: Record<string, string[]> = {
  甲: ['亥','子','丑','寅','卯','辰','巳','午','未','申','酉','戌'],
  乙: ['午','巳','辰','卯','寅','丑','子','亥','戌','酉','申','未'],
  丙: ['寅','卯','辰','巳','午','未','申','酉','戌','亥','子','丑'],
  丁: ['酉','申','未','午','巳','辰','卯','寅','丑','子','亥','戌'],
  戊: ['寅','卯','辰','巳','午','未','申','酉','戌','亥','子','丑'],
  己: ['酉','申','未','午','巳','辰','卯','寅','丑','子','亥','戌'],
  庚: ['巳','午','未','申','酉','戌','亥','子','丑','寅','卯','辰'],
  辛: ['子','亥','戌','酉','申','未','午','巳','辰','卯','寅','丑'],
  壬: ['申','酉','戌','亥','子','丑','寅','卯','辰','巳','午','未'],
  癸: ['卯','寅','丑','子','亥','戌','酉','申','未','午','巳','辰'],
};
const XUNCHANG_TABLE: Record<string, { xunShou: string; kongWang: string }> = {
  甲子: { xunShou: '甲子旬', kongWang: '戌亥' },
  甲戌: { xunShou: '甲戌旬', kongWang: '申酉' },
  甲申: { xunShou: '甲申旬', kongWang: '午未' },
  甲午: { xunShou: '甲午旬', kongWang: '辰巳' },
  甲辰: { xunShou: '甲辰旬', kongWang: '寅卯' },
  甲寅: { xunShou: '甲寅旬', kongWang: '子丑' },
};
const NAYIN_MAP: Record<string, string> = {
  甲子: '海中金', 乙丑: '海中金', 丙寅: '炉中火', 丁卯: '炉中火',
  戊辰: '大林木', 己巳: '大林木', 庚午: '路旁土', 辛未: '路旁土',
  壬申: '剑锋金', 癸酉: '剑锋金', 甲戌: '山头火', 乙亥: '山头火',
  丙子: '涧下水', 丁丑: '涧下水', 戊寅: '城头土', 己卯: '城头土',
  庚辰: '白蜡金', 辛巳: '白蜡金', 壬午: '杨柳木', 癸未: '杨柳木',
  甲申: '泉中水', 乙酉: '泉中水', 丙戌: '屋上土', 丁亥: '屋上土',
  戊子: '霹雳火', 己丑: '霹雳火', 庚寅: '松柏木', 辛卯: '松柏木',
  壬辰: '长流水', 癸巳: '长流水', 甲午: '沙中金', 乙未: '沙中金',
  丙申: '山下火', 丁酉: '山下火', 戊戌: '平地木', 己亥: '平地木',
  庚子: '壁上土', 辛丑: '壁上土', 壬寅: '金箔金', 癸卯: '金箔金',
  甲辰: '覆灯火', 乙巳: '覆灯火', 丙午: '天河水', 丁未: '天河水',
  戊申: '大驿土', 己酉: '大驿土', 庚戌: '钗钏金', 辛亥: '钗钏金',
  壬子: '桑柘木', 癸丑: '桑柘木', 甲寅: '大溪水', 乙卯: '大溪水',
  丙辰: '沙中土', 丁巳: '沙中土', 戊午: '天上火', 己未: '天上火',
  庚申: '石榴木', 辛酉: '石榴木', 壬戌: '大海水', 癸亥: '大海水',
};

function sortShensha(items: ShenshaItem[]): ShenshaItem[] {
  return [...items].sort((a, b) => {
    const byLevel = (LEVEL_WEIGHT[b.level] || 0) - (LEVEL_WEIGHT[a.level] || 0);
    if (byLevel !== 0) return byLevel;
    return b.influenceScore - a.influenceScore;
  });
}

function buildTransitPillars(fp: FourPillars, stem: string, branch: string): FourPillars {
  return {
    ...fp,
    hour: { stem: stem as Stem, branch: branch as Branch, label: '时柱' },
  };
}

function getBranchMainStem(branch: string): Stem | undefined {
  return BRANCH_HIDDEN[branch as Branch]?.[0]?.stem;
}

function getStemColor(stem?: string): string {
  return stem && STEM_ELEMENT[stem as Stem] ? ELEMENT_COLORS[STEM_ELEMENT[stem as Stem]] : '#E8E0E8';
}

function getBranchColor(branch?: string): string {
  const mainStem = branch ? getBranchMainStem(branch) : undefined;
  return mainStem ? getStemColor(mainStem) : '#E8C39E';
}

function getChangSheng(stem?: string, branch?: string): string {
  if (!stem || !branch) return '';
  const stages = CHANG_SHENG_MAP[stem];
  if (!stages) return '';
  const idx = stages.indexOf(branch);
  return idx >= 0 ? CHANG_SHENG_NAMES[idx] : '';
}

function getKongWang(stem?: string, branch?: string): string {
  if (!stem || !branch) return '';
  const si = STEMS.indexOf(stem as Stem);
  const bi = BRANCHES.indexOf(branch as Branch);
  if (si < 0 || bi < 0) return '';
  const xunStartBi = ((bi - si) % 12 + 12) % 12;
  const xunKey = `甲${BRANCHES[xunStartBi]}`;
  return XUNCHANG_TABLE[xunKey]?.kongWang || '';
}

function getNaYin(stem?: string, branch?: string): string {
  return stem && branch ? (NAYIN_MAP[`${stem}${branch}`] || '') : '';
}

function buildHiddenStemLabels(dayStem: string, branch: string): Array<{ stem: string; star: string; color: string }> {
  return (BRANCH_HIDDEN[branch as Branch] || []).map(item => ({
    stem: item.stem,
    star: getShishen(dayStem as Stem, item.stem),
    color: getStemColor(item.stem),
  }));
}

function calcTransitShensha(fp: FourPillars, stem: string, branch: string, lang: string): ShenshaItem[] {
  if (!stem || !branch) return [];
  const transitFp = buildTransitPillars(fp, stem, branch);
  return sortShensha(calcShensha(transitFp, lang).filter(item => item.pillars.some(pillar => TRANSIT_PILLAR_LABELS.has(pillar))));
}

function groupNatalShenshaByPillar(items: ShenshaItem[]): Record<string, ShenshaItem[]> {
  const groups: Record<string, ShenshaItem[]> = { 年柱: [], 月柱: [], 日柱: [], 时柱: [] };
  items.forEach(item => {
    item.pillars.forEach(pillar => {
      if (groups[pillar]) groups[pillar].push(item);
    });
  });
  Object.keys(groups).forEach(key => { groups[key] = sortShensha(groups[key]); });
  return groups;
}

function splitHiddenStem(cangGan: string): { stem: string; star: string } {
  if (!cangGan) return { stem: '', star: '' };
  return { stem: cangGan.slice(0, 1), star: cangGan.slice(1) };
}

function ProfessionalFourPillarGrid({
  pillars,
  natalGroups,
  activeLiunian,
  activeDayun,
  mingGong,
  isDark,
  rowBorder,
  labelColor,
  valueColor,
  onShenshaClick,
  children,
}: {
  pillars: Pillar[];
  natalGroups: Record<string, ShenshaItem[]>;
  activeLiunian?: TransitShensha;
  activeDayun?: TransitShensha;
  mingGong?: MingGongData;
  isDark: boolean;
  rowBorder: string;
  labelColor: string;
  valueColor: string;
  onShenshaClick: (item: ShenshaItem, context: string) => void;
  children?: ReactNode;
}) {
  const { t, i18n } = useTranslation('paipan');
  const mainPillars = pillars.slice(0, 4);
  const extraPillars = pillars.slice(4, 6);
  const isZh = i18n.language.startsWith('zh');
  const dayStem = mainPillars[2]?.tianGan || '甲';
  const columns = [
    activeLiunian ? { key: 'liunian', label: t('professionalPaipan.liunianColumn'), transit: activeLiunian, highlight: false } : null,
    activeDayun ? { key: 'dayun', label: t('professionalPaipan.dayunColumn'), transit: activeDayun, highlight: false } : null,
    ...mainPillars.map((pillar, idx) => ({
      key: pillar.label,
      label: t(`professionalPaipan.pillar.${PILLAR_KEYS[idx]}`, { defaultValue: pillar.label }),
      pillar,
      pillarKey: PILLAR_KEYS[idx],
      highlight: !!pillar.isHighlight,
    })),
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    pillar?: Pillar;
    pillarKey?: typeof PILLAR_KEYS[number];
    transit?: TransitShensha;
    highlight: boolean;
  }>;
  const gridLabelW = 44;
  const gridColW = 42;
  const gridTemplate = `${gridLabelW}px repeat(${columns.length}, minmax(${gridColW}px, 1fr))`;
  const highlightBg = isDark ? 'rgba(232,195,158,0.08)' : 'rgba(232,160,32,0.08)';
  const subtleBg = isDark ? 'rgba(183,154,197,0.035)' : 'rgba(232,160,32,0.035)';
  const gridSurface = getProfessionalGridSurface(isDark);
  const getColumnStem = (column: typeof columns[number]) => column.pillar?.tianGan || column.transit?.stem || '';
  const getColumnBranch = (column: typeof columns[number]) => column.pillar?.diZhi || column.transit?.branch || '';
  const getColumnMainStar = (column: typeof columns[number]) => column.pillar?.mainStar || column.transit?.stemShishen || '--';
  const getColumnHidden = (column: typeof columns[number]) => {
    if (column.pillar) {
      return column.pillar.cangGan.map((item, idx) => {
        const hidden = splitHiddenStem(item);
        return { ...hidden, color: column.pillar?.cangGanColors?.[idx] || getStemColor(hidden.stem) };
      });
    }
    return buildHiddenStemLabels(dayStem, getColumnBranch(column));
  };
  const getColumnShensha = (column: typeof columns[number]) => {
    if (column.pillarKey) return natalGroups[column.pillarKey] || [];
    return column.transit?.items || [];
  };
  const getStemCellColor = (column: typeof columns[number]) => column.pillar?.tianGanColor || getStemColor(getColumnStem(column));
  const getBranchCellColor = (column: typeof columns[number]) => column.pillar?.diZhiColor || getBranchColor(getColumnBranch(column));
  const Cell = ({
    children,
    highlight,
    className = '',
  }: {
    children: ReactNode;
    highlight?: boolean;
    className?: string;
  }) => (
    <div
      className="min-w-0 flex items-center justify-center text-center px-1 py-1.5"
      style={{ background: highlight ? highlightBg : undefined }}
    >
      {children}
    </div>
  );
  const LabelCell = ({ children }: { children: ReactNode }) => (
    <div className="flex items-center justify-center text-center px-1 py-1.5" style={{ color: labelColor }}>
      <span className="text-[9px] leading-tight">{children}</span>
    </div>
  );
  const Row = ({ children, tall = false }: { children: ReactNode; tall?: boolean }) => (
    <div
      className={`grid border-b ${tall ? 'min-h-[58px]' : ''}`}
      style={{ gridTemplateColumns: gridTemplate, borderColor: rowBorder }}
    >
      {children}
    </div>
  );
  const stemBranchSize = 'text-lg';

  return (
    <section>
      <div className="overflow-x-auto -mx-3 px-3" style={{ scrollbarWidth: 'none' }}>
        <div className="rounded-2xl overflow-hidden border" style={{ borderColor: rowBorder, background: gridSurface }}>
          <Row>
            <LabelCell>{t('basicPaipan.col.date')}</LabelCell>
            {columns.map(column => (
              <Cell key={column.key} highlight={column.highlight}>
                <span className="text-[10px] font-bold" style={{ color: column.highlight ? 'var(--accent-secondary)' : 'var(--accent-primary)', fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>
                  {column.label}
                </span>
              </Cell>
            ))}
          </Row>

          <Row>
            <LabelCell>{t('basicPaipan.col.mainStar')}</LabelCell>
            {columns.map(column => (
              <Cell key={`star-${column.key}`} highlight={column.highlight}>
                <span className="text-[10px] font-bold leading-tight" style={{ color: column.highlight ? 'var(--accent-secondary)' : valueColor, fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>
                  {getColumnMainStar(column)}
                </span>
              </Cell>
            ))}
          </Row>

          <Row tall>
            <LabelCell>{t('basicPaipan.col.tianGan')}</LabelCell>
            {columns.map(column => (
              <Cell key={`stem-${column.key}`} highlight={column.highlight}>
                <span className={`${stemBranchSize} font-bold`} style={{ color: getStemCellColor(column), fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>
                  {getColumnStem(column) || '--'}
                </span>
              </Cell>
            ))}
          </Row>

          <Row tall>
            <LabelCell>{t('basicPaipan.col.diZhi')}</LabelCell>
            {columns.map(column => (
              <Cell key={`branch-${column.key}`} highlight={column.highlight}>
                <span className={`${stemBranchSize} font-bold`} style={{ color: getBranchCellColor(column), fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>
                  {getColumnBranch(column) || '--'}
                </span>
              </Cell>
            ))}
          </Row>

          <Row tall>
            <LabelCell>{t('professionalPaipan.hiddenAndSub')}</LabelCell>
            {columns.map(column => {
              const hidden = getColumnHidden(column);
              return (
                <Cell key={`hidden-${column.key}`} highlight={column.highlight} className="items-start">
                  <div className="flex flex-col gap-1">
                    {hidden.length ? hidden.map((item, idx) => (
                      <div key={`${column.key}-${item.stem}-${idx}`} className="leading-tight">
                        <span className="text-[9px] font-bold" style={{ color: item.color }}>{item.stem}</span>
                        <span className="text-[8px] ml-0.5" style={{ color: item.color }}>{item.star}</span>
                      </div>
                    )) : <span className="text-[8px]" style={{ color: labelColor }}>--</span>}
                  </div>
                </Cell>
              );
            })}
          </Row>

          {[
            { label: t('basicPaipan.col.xingYun'), getValue: (column: typeof columns[number]) => column.pillar?.xingYun || getChangSheng(dayStem, getColumnBranch(column)) },
            { label: t('basicPaipan.col.ziZuo'), getValue: (column: typeof columns[number]) => column.pillar?.ziZuo || getChangSheng(getColumnStem(column), getColumnBranch(column)) },
            { label: t('basicPaipan.col.kongWang'), getValue: (column: typeof columns[number]) => column.pillar?.kongWang || getKongWang(getColumnStem(column), getColumnBranch(column)) },
            { label: t('basicPaipan.col.naYin'), getValue: (column: typeof columns[number]) => column.pillar?.naYin || getNaYin(getColumnStem(column), getColumnBranch(column)), small: true },
           ].map(row => (
            <Row key={row.label}>
              <LabelCell>{row.label}</LabelCell>
              {columns.map(column => (
                <Cell key={`${row.label}-${column.key}`} highlight={column.highlight}>
                  <span className={`${row.small ? 'text-[9px]' : 'text-[10px]'} font-bold leading-tight`} style={{ color: row.label === t('basicPaipan.col.kongWang') ? 'var(--accent-secondary)' : valueColor, fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>
                    {row.getValue(column) || '--'}
                  </span>
                </Cell>
              ))}
            </Row>
           ))}

          <div className="grid" style={{ gridTemplateColumns: gridTemplate, background: subtleBg }}>
            <LabelCell>{t('basicPaipan.col.shenSha')}</LabelCell>
            {columns.map(column => {
              const items = getColumnShensha(column);
              return (
                <Cell key={`shensha-${column.key}`} highlight={column.highlight} className="items-start">
                  <div className="flex flex-col items-center gap-1 py-1">
                    {items.length ? items.slice(0, 12).map(item => (
                      <button
                        type="button"
                        key={`${column.key}-${item.name}`}
                        className="text-[9px] leading-tight active:opacity-70"
                        style={{ color: item.color }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onShenshaClick(item, `${column.label} · ${t('basicPaipan.col.shenSha')}`);
                        }}
                      >
                        {item.name}
                      </button>
                    )) : <span className="text-[8px]" style={{ color: labelColor }}>--</span>}
                  </div>
                </Cell>
              );
            })}
          </div>

          {children}
        </div>
      </div>

      {((extraPillars.length > 0) || mingGong) && (
        <div className="mt-3 rounded-2xl overflow-hidden border" style={{ borderColor: rowBorder, background: gridSurface }}>
          <div className="grid divide-x" style={{ gridTemplateColumns: `repeat(${extraPillars.length + (mingGong ? 1 : 0)}, minmax(0, 1fr))`, borderColor: rowBorder }}>
            {extraPillars.map(pillar => (
              <div key={pillar.label} className="py-3 px-2 flex flex-col items-center gap-1" style={{ borderColor: rowBorder }}>
                <span className="text-[10px]" style={{ color: labelColor }}>{pillar.label}</span>
                <div className="flex items-center gap-1">
                  <span className={`${stemBranchSize} font-bold`} style={{ color: pillar.tianGanColor || valueColor, fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>{pillar.tianGan}</span>
                  <span className={`${stemBranchSize} font-bold`} style={{ color: pillar.diZhiColor || 'var(--accent-secondary)', fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>{pillar.diZhi}</span>
                </div>
                <span className="text-[10px]" style={{ color: labelColor }}>{pillar.naYin}</span>
              </div>
            ))}
            {mingGong && (
              <div className="py-3 px-2 flex flex-col items-center gap-1">
                <span className="text-[10px]" style={{ color: labelColor }}>{t('basicPaipan.mingGong')}</span>
                <div className="flex items-center gap-1">
                  <span className={`${stemBranchSize} font-bold`} style={{ color: mingGong.tianGanColor || valueColor, fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>{mingGong.tianGan}</span>
                  <span className={`${stemBranchSize} font-bold`} style={{ color: mingGong.diZhiColor || 'var(--accent-secondary)', fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>{mingGong.diZhi}</span>
                </div>
                <span className="text-[10px]" style={{ color: labelColor }}>{mingGong.naYin}</span>
              </div>
            )}
          </div>
        </div>
      )}

    </section>
  );
}

function CompactWuxingPanel({
  fourPillars,
  isDark,
  compact,
  isZh,
  rowBorder,
  labelColor,
  valueColor,
  accentColor,
  goldColor,
}: {
  fourPillars: FourPillars;
  isDark: boolean;
  compact?: boolean;
  isZh: boolean;
  rowBorder: string;
  labelColor: string;
  valueColor: string;
  accentColor: string;
  goldColor: string;
}) {
  const [activeMode, setActiveMode] = useState<'energy' | 'visible' | 'hidden'>('energy');
  const [expandedElement, setExpandedElement] = useState<Element | null>(null);
  const [activeSection, setActiveSection] = useState<Partial<Record<Element, string>>>({});
  const { i18n } = useTranslation('paipan');
  const lang = i18n.language;
  const isLocale = ['en', 'ko', 'ja'].includes(lang);

  const getFixed = (element: string) => {
    if (isLocale) return getWuxingFixedLocale(element, lang);
    return null;
  };
  const getAnalysis = (element: string, level: string) => {
    if (isLocale) return getWuxingAnalysisLocale(element, level, lang);
    return null;
  };
  const ltext = (text: string) => convertText(text, lang);

  const energyItems = useMemo(() => buildWuxingDetail(fourPillars), [fourPillars]);
  const visibleItems = useMemo(() => buildWuxingVisibleCounts(fourPillars), [fourPillars]);
  const hiddenItems = useMemo(() => buildWuxingHiddenCounts(fourPillars), [fourPillars]);
  const items = activeMode === 'energy' ? energyItems : activeMode === 'visible' ? visibleItems : hiddenItems;
  const overview = useMemo(() => getWuxingOverview(energyItems), [energyItems]);
  const seasonStates = useMemo(() => getWuxingSeasonStates(fourPillars.month.branch), [fourPillars]);

  const toggleExpand = (el: Element) => {
    setExpandedElement(prev => prev === el ? null : el);
    if (!activeSection[el]) {
      setActiveSection(prev => ({ ...prev, [el]: 'basis' }));
    }
  };

  const modes = [
    { key: 'energy' as const, label: isZh ? ltext('五行能量') : 'Energy' },
    { key: 'visible' as const, label: isZh ? ltext('五行个数') : '8 Chars' },
    { key: 'hidden' as const, label: isZh ? ltext('含藏干数') : 'Hidden Stems' },
  ];

  const sections = [
    { key: 'basis', label: isZh ? ltext('旺衰依据') : 'Basis', icon: 'ri-scales-3-line' },
    { key: 'personality', label: isZh ? ltext('性格') : 'Personality', icon: 'ri-user-smile-line' },
    { key: 'career', label: isZh ? ltext('事业') : 'Career', icon: 'ri-briefcase-line' },
    { key: 'emotion', label: isZh ? ltext('感情') : 'Emotion', icon: 'ri-heart-line' },
    { key: 'health', label: isZh ? ltext('健康') : 'Health', icon: 'ri-heart-pulse-line' },
    { key: 'balance', label: isZh ? ltext('平衡') : 'Balance', icon: 'ri-refresh-line' },
  ];

  const formatPercent = (value: number) => Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;

  return (
    <div className={`${compact ? 'mt-1.5' : 'mt-3'} rounded-2xl overflow-hidden border`} style={{ borderColor: rowBorder, background: isDark ? 'rgba(107,78,113,0.10)' : 'rgba(245,245,247,0.72)' }}>
      <div className={`flex items-center justify-between border-b ${compact ? 'px-2 py-2' : 'px-3 py-2.5'}`} style={{ borderColor: rowBorder }}>
        <div className="flex items-center gap-2">
          <div className="w-1 h-3 rounded-full" style={{ background: goldColor }} />
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-bold`} style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>
            {isZh ? ltext('五行详析') : 'Five Elements'}
          </span>
          <span className={`${compact ? 'text-[9px]' : 'text-[10px]'}`} style={{ color: labelColor }}>
            {isZh ? ltext(`平衡度 ${overview.balanceScore}%`) : `Balance ${overview.balanceScore}%`}
          </span>
        </div>
      </div>

      <div className={`${compact ? 'px-2 py-2' : 'px-3 py-3'}`}>
        <div className="grid grid-cols-3 gap-1 p-1 rounded-xl mb-3" style={{ background: isDark ? 'rgba(183,154,197,0.12)' : 'rgba(232,160,32,0.08)' }}>
          {modes.map(mode => (
            <button
              key={mode.key}
              type="button"
              onClick={() => setActiveMode(mode.key)}
              className={`${compact ? 'h-8 text-[10px]' : 'h-10 text-sm'} rounded-lg font-medium transition-all`}
              style={{
                color: activeMode === mode.key ? valueColor : labelColor,
                background: activeMode === mode.key ? (isDark ? 'rgba(183,154,197,0.28)' : 'rgba(255,255,255,0.72)') : 'transparent',
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {energyItems.map((item) => {
            const displayItem = items.find(it => it.element === item.element) || item;
            const isOpen = expandedElement === item.element;
            const curSection = activeSection[item.element] || 'basis';
            const fixed = item.fixed;
            const localFixed = getFixed(item.element);
            const localAnalysis = getAnalysis(item.element, item.level);

            return (
              <div key={item.element} className="rounded-xl border overflow-hidden" style={{ borderColor: isOpen ? `${item.color}40` : rowBorder, background: isDark ? 'rgba(107,78,113,0.06)' : 'rgba(255,255,255,0.45)' }}>
                <button
                  type="button"
                  onClick={() => toggleExpand(item.element)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                >
                  <div className={`${compact ? 'w-7 h-7 rounded-lg' : 'w-8 h-8 rounded-xl'} flex items-center justify-center flex-shrink-0`} style={{ background: `${item.color}20`, border: `1.5px solid ${item.color}50` }}>
                    <i className={`${item.icon} ${compact ? 'text-sm' : 'text-base'}`} style={{ color: item.color }}></i>
                  </div>
                  <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-bold flex-shrink-0`} style={{ color: item.color }}>{item.element}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(107,78,113,0.3)' : 'rgba(232,160,32,0.12)' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.min(displayItem.percent, 100)}%`, background: item.color, opacity: displayItem.percent < 15 ? 0.5 : 1 }}
                    />
                  </div>
                  <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-right font-bold flex-shrink-0 whitespace-nowrap`} style={{ color: activeMode === 'energy' ? item.levelColor : item.color }}>
                    {activeMode === 'energy' ? formatPercent(displayItem.percent) : `${formatPercent(displayItem.percent)} · ${displayItem.count || 0}${ltext('个')}`}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: isOpen ? `${item.color}20` : (isDark ? 'rgba(183,154,197,0.1)' : 'rgba(232,160,32,0.08)') }}>
                    <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`} style={{ color: isOpen ? item.color : accentColor }} />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pt-1 border-t" style={{ borderColor: `${item.color}20` }}>
                    <div className="rounded-xl p-3 mb-3 border" style={{ background: `${item.color}08`, borderColor: `${item.color}25` }}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}15`, border: `1.5px solid ${item.color}40` }}>
                          <i className={`${ELEMENT_ICONS[item.element]} text-sm`} style={{ color: item.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`${compact ? 'text-sm' : 'text-base'} font-bold`} style={{ color: item.color, fontFamily: "'Noto Serif TC', serif" }}>
                              {localAnalysis ? localAnalysis.title : ltext(item.analysis.title)}
                            </span>
                            <span className={`${compact ? 'text-[9px]' : 'text-xs'} font-bold px-1.5 py-0.5 rounded-full`} style={{ background: `${item.levelColor}20`, color: item.levelColor }}>
                              {getStrengthLevelLabel(item.level, lang)}
                            </span>
                            <span className={`${compact ? 'text-[9px]' : 'text-xs'}`} style={{ color: labelColor }}>{item.percent}%</span>
                          </div>
                          <p className={`${compact ? 'text-[9px]' : 'text-xs'} mt-0.5`} style={{ color: labelColor }}>
                            {localFixed ? `${localFixed.keyword} · ${localFixed.symbol}` : `${ltext(fixed.keyword)} · ${ltext(fixed.symbol)}`}
                          </p>
                        </div>
                      </div>
                      <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed mt-1`} style={{ color: labelColor }}>
                        {localAnalysis ? localAnalysis.summary : ltext(item.analysis.summary)}
                      </p>
                    </div>

                    <div className="flex gap-1 mb-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                      {sections.map(sec => (
                        <button
                          key={sec.key}
                          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg flex-shrink-0 border transition-all ${compact ? 'text-[10px]' : 'text-xs'}`}
                          style={{
                            background: curSection === sec.key ? `${item.color}15` : (isDark ? 'rgba(107,78,113,0.2)' : 'rgba(255,255,255,0.6)'),
                            borderColor: curSection === sec.key ? `${item.color}40` : rowBorder,
                            color: curSection === sec.key ? item.color : labelColor,
                            fontWeight: curSection === sec.key ? 700 : 400,
                          }}
                          onClick={() => setActiveSection(prev => ({ ...prev, [item.element]: sec.key }))}
                        >
                          <i className={`${sec.icon} text-xs`} />
                          <span className="whitespace-nowrap">{sec.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="rounded-xl p-3 border mb-3" style={{ background: isDark ? 'rgba(107,78,113,0.12)' : 'rgba(255,255,255,0.58)', borderColor: `${item.color}20` }}>
                      {curSection === 'basis' && (() => {
                        const visible = visibleItems.find(entry => entry.element === item.element)?.count ?? 0;
                        const hidden = hiddenItems.find(entry => entry.element === item.element)?.count ?? 0;
                        const season = seasonStates.find(entry => entry.element === item.element)?.state ?? '休';
                        return (
                          <div>
                            <div className="flex items-center gap-1.5 mb-2">
                              <i className="ri-scales-3-line text-xs" style={{ color: item.color }}></i>
                              <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>{isZh ? ltext('本盘旺衰依据') : 'Chart Basis'}</span>
                            </div>
                            <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed`} style={{ color: labelColor }}>
                              {isZh
                                ? ltext(`「${item.element}」表面可见计数为 ${visible}，纳入全部藏干后为 ${hidden}；在${fourPillars.month.branch}月处于「${season}」位。综合天干、藏干权重、月令加成与根气后，最终能量为 ${item.percent}%，判为「${item.level}」。因此这里的强弱可能与肉眼数出来的个数不同。`)
                                : `${item.element}: ${visible} visible, ${hidden} including hidden stems, season state ${season}; weighted energy ${item.percent}% (${item.level}).`}
                            </p>
                          </div>
                        );
                      })()}
                      {curSection === 'personality' && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <i className="ri-user-smile-line text-xs" style={{ color: item.color }}></i>
                            <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>{isZh ? ltext('性格') : 'Personality'}</span>
                          </div>
                          <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed`} style={{ color: labelColor }}>
                            {localAnalysis ? localAnalysis.personality : ltext(item.analysis.personality)}
                          </p>
                        </div>
                      )}
                      {curSection === 'career' && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <i className="ri-briefcase-line text-xs" style={{ color: item.color }}></i>
                            <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>{isZh ? ltext('事业') : 'Career'}</span>
                          </div>
                          <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed mb-3`} style={{ color: labelColor }}>
                            {localAnalysis ? localAnalysis.career : ltext(item.analysis.career)}
                          </p>
                          <div className="border-t pt-2" style={{ borderColor: rowBorder }}>
                            <p className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold mb-1.5`} style={{ color: item.color }}>{isZh ? ltext('适合领域') : 'Suitable Fields'}</p>
                            <div className="flex flex-wrap gap-1">
                              {(localFixed ? localFixed.careers : fixed.careers).map((c, i) => (
                                <span key={i} className={`${compact ? 'text-[9px]' : 'text-xs'} px-1.5 py-0.5 rounded-full`}
                                  style={{ background: `${item.color}12`, color: item.color, border: `1px solid ${item.color}25` }}>
                                  {localFixed ? c : ltext(c)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {curSection === 'emotion' && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <i className="ri-heart-line text-xs" style={{ color: item.color }}></i>
                            <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>{isZh ? ltext('感情') : 'Emotion'}</span>
                          </div>
                          <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed mb-3`} style={{ color: labelColor }}>
                            {localAnalysis ? localAnalysis.emotion : ltext(item.analysis.emotion)}
                          </p>
                          <div className="grid grid-cols-2 gap-2 border-t pt-2" style={{ borderColor: rowBorder }}>
                            {[
                              { label: isZh ? ltext('情志') : 'Emotion', value: localFixed ? localFixed.emotion : ltext(fixed.emotion), icon: 'ri-emotion-line' },
                              { label: isZh ? ltext('季节') : 'Season', value: localFixed ? localFixed.season : ltext(fixed.season), icon: 'ri-sun-line' },
                              { label: isZh ? ltext('味道') : 'Taste', value: localFixed ? localFixed.taste : ltext(fixed.taste), icon: 'ri-cup-line' },
                              { label: isZh ? ltext('方位') : 'Direction', value: localFixed ? localFixed.direction : ltext(fixed.direction), icon: 'ri-compass-line' },
                            ].map((attr, i) => (
                              <div key={i} className="flex items-start gap-1.5">
                                <i className={`${attr.icon} text-xs flex-shrink-0 mt-0.5`} style={{ color: item.color }}></i>
                                <div>
                                  <p className={`${compact ? 'text-[9px]' : 'text-xs'}`} style={{ color: labelColor }}>{attr.label}</p>
                                  <p className={`${compact ? 'text-[9px]' : 'text-xs'} font-bold`} style={{ color: valueColor }}>{attr.value}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {curSection === 'health' && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <i className="ri-heart-pulse-line text-xs" style={{ color: item.color }}></i>
                            <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>{isZh ? ltext('健康') : 'Health'}</span>
                          </div>
                          <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed mb-3`} style={{ color: labelColor }}>
                            {localAnalysis ? localAnalysis.health : ltext(item.analysis.health)}
                          </p>
                          <div className="grid grid-cols-2 gap-2 border-t pt-2" style={{ borderColor: rowBorder }}>
                            <div className="rounded-lg p-2" style={{ background: `${item.color}08` }}>
                              <p className={`${compact ? 'text-[10px]' : 'text-sm'} mb-0.5`} style={{ color: labelColor }}>{isZh ? ltext('主脏') : 'Main Organ'}</p>
                              <p className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>
                                {localFixed ? localFixed.organ : ltext(fixed.organ)}
                              </p>
                            </div>
                            <div className="rounded-lg p-2" style={{ background: `${item.color}08` }}>
                              <p className={`${compact ? 'text-[10px]' : 'text-sm'} mb-0.5`} style={{ color: labelColor }}>{isZh ? ltext('相关器官') : 'Related'}</p>
                              <p className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>
                                {localFixed ? localFixed.organRelated : ltext(fixed.organRelated)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {curSection === 'balance' && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <i className="ri-refresh-line text-xs" style={{ color: item.color }}></i>
                            <span className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold`} style={{ color: item.color }}>{isZh ? ltext('平衡') : 'Balance'}</span>
                          </div>
                          <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed mb-3`} style={{ color: labelColor }}>
                            {localAnalysis ? localAnalysis.balance : ltext(item.analysis.balance)}
                          </p>
                          {(item.level === '极弱' || item.level === '偏弱') && (
                            <div className="border-t pt-2" style={{ borderColor: rowBorder }}>
                              <p className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold mb-2`} style={{ color: '#7BC67E' }}>
                                <i className="ri-add-circle-line mr-1"></i>{isZh ? ltext(`补充${getElementLabel(item.element, lang)}`) : `Boost ${item.element}`}
                              </p>
                              <div className="space-y-1">
                                {(localFixed ? localFixed.boostTips : fixed.boostTips).map((tip, i) => (
                                  <div key={i} className="flex items-start gap-1.5">
                                    <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#7BC67E' }}></div>
                                    <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed`} style={{ color: labelColor }}>
                                      {localFixed ? tip : ltext(tip)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(item.level === '过旺' || item.level === '旺盛') && (
                            <div className="border-t pt-2" style={{ borderColor: rowBorder }}>
                              <p className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold mb-2`} style={{ color: '#E8A020' }}>
                                <i className="ri-subtract-line mr-1"></i>{isZh ? ltext(`降低${getElementLabel(item.element, lang)}`) : `Reduce ${item.element}`}
                              </p>
                              <div className="space-y-1">
                                {(localFixed ? localFixed.reduceTips : fixed.reduceTips).map((tip, i) => (
                                  <div key={i} className="flex items-start gap-1.5">
                                    <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#E8A020' }}></div>
                                    <p className={`${compact ? 'text-[10px]' : 'text-sm'} leading-relaxed`} style={{ color: labelColor }}>
                                      {localFixed ? tip : ltext(tip)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl p-3 border" style={{ background: isDark ? 'rgba(107,78,113,0.12)' : 'rgba(255,255,255,0.58)', borderColor: rowBorder }}>
                      <p className={`${compact ? 'text-[10px]' : 'text-sm'} font-bold mb-2`} style={{ color: accentColor }}>{isZh ? ltext('五行关系') : 'Element Relations'}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: `${getElementLabel(fixed.generates, lang)}${isZh ? ltext('我生') : ' generates'}`, el: fixed.generates, icon: 'ri-arrow-right-line', desc: getWuxingRelDesc(item.element, 'generates', fixed.generates, lang), type: 'neutral' },
                          { label: `${getElementLabel(fixed.overcomes, lang)}${isZh ? ltext('我克') : ' overcomes'}`, el: fixed.overcomes, icon: 'ri-scales-line', desc: getWuxingRelDesc(item.element, 'overcomes', fixed.overcomes, lang), type: 'positive' },
                          { label: `${getElementLabel(fixed.generatedBy, lang)}${isZh ? ltext('生我') : ' generated by'}`, el: fixed.generatedBy, icon: 'ri-heart-add-line', desc: getWuxingRelDesc(item.element, 'generatedBy', fixed.generatedBy, lang), type: 'positive' },
                          { label: `${getElementLabel(fixed.overcomesBy, lang)}${isZh ? ltext('克我') : ' overcomes by'}`, el: fixed.overcomesBy, icon: 'ri-shield-line', desc: getWuxingRelDesc(item.element, 'overcomesBy', fixed.overcomesBy, lang), type: 'caution' },
                        ].map((rel, i) => {
                          const relColor = ELEMENT_COLORS[rel.el as Element];
                          return (
                            <div key={i} className="rounded-lg p-2 border"
                              style={{ background: `${relColor}08`, borderColor: `${relColor}25` }}>
                              <div className="flex items-center gap-1 mb-1">
                                <i className={`${rel.icon} text-xs`} style={{ color: relColor }}></i>
                                <span className={`${compact ? 'text-[9px]' : 'text-xs'} font-bold`} style={{ color: relColor }}>{rel.label}</span>
                              </div>
                              <p className={`${compact ? 'text-[9px]' : 'text-xs'} leading-relaxed`} style={{ color: labelColor }}>{rel.desc}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-2.5 py-2 rounded-xl mt-3 mb-2" style={{ background: isDark ? 'rgba(183,154,197,0.08)' : 'rgba(232,160,32,0.06)' }}>
          <p className={`${compact ? 'text-[9px]' : 'text-[10px]'} leading-relaxed`} style={{ color: isDark ? 'rgba(232,224,232,0.7)' : '#5A4A35' }}>
            {overview.summary}
          </p>
        </div>
        {activeMode === 'energy' && (
          <p className={`${compact ? 'text-[8px]' : 'text-[9px]'} leading-relaxed mb-2`} style={{ color: labelColor }}>
            {isZh ? ltext('注：五行能量受出生地与出生时辰共同影响。若出生地缺失、经度差较大，或出生时刻临近时辰交界，命盘可能出现时柱与比例差异。') : 'Note: element energy is affected by birth place and birth hour.'}
          </p>
        )}

        {activeMode !== 'energy' && (
          <div className="grid grid-cols-5 gap-1 mb-2">
            {items.map(it => (
              <div key={it.element} className="rounded-lg py-2 text-center" style={{ background: isDark ? 'rgba(183,154,197,0.08)' : 'rgba(255,255,255,0.58)' }}>
                <div className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-bold`} style={{ color: it.color }}>{it.element}{it.count || 0}</div>
                <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} mt-0.5`} style={{ color: labelColor }}>{it.role}</div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-5 gap-1 pt-2 border-t" style={{ borderColor: isDark ? 'rgba(183,154,197,0.12)' : 'rgba(0,0,0,0.06)' }}>
          {seasonStates.map(it => (
            <div key={it.element} className="text-center">
              <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold`} style={{ color: it.color }}>{it.element}</span>
              <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} ml-0.5`} style={{ color: valueColor }}>{it.state}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RiskNotice({ isDark }: { isDark: boolean }) {
  const { t } = useTranslation('paipan');
  return (
    <div
      className="rounded-xl p-3 border"
      style={{
        borderColor: isDark ? 'rgba(183,154,197,0.14)' : 'rgba(232,160,32,0.18)',
        background: isDark ? 'rgba(183,154,197,0.06)' : 'rgba(232,160,32,0.05)',
      }}
    >
      <div className="flex items-start gap-2">
        <i className="ri-information-line text-sm mt-0.5" style={{ color: 'var(--accent-secondary)' }} />
        <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          {t('paipanRisk.notice')}
        </p>
      </div>
    </div>
  );
}

function ShenshaDetailModal({
  selected,
  onClose,
  isDark,
}: {
  selected: SelectedShensha | null;
  onClose: () => void;
  isDark: boolean;
}) {
  const { t, i18n } = useTranslation('paipan');
  if (!selected) return null;
  const { item, context } = selected;
  const overlayBg = isDark ? 'rgba(15,10,18,0.72)' : 'rgba(60,50,40,0.45)';
  const sheetBg = isDark ? 'linear-gradient(180deg,#24162F 0%,#171020 100%)' : 'linear-gradient(180deg,#FFFBF5 0%,#FFF5E8 100%)';
  const borderColor = isDark ? 'rgba(183,154,197,0.24)' : 'rgba(232,160,32,0.26)';
  const sectionBg = isDark ? 'rgba(183,154,197,0.08)' : 'rgba(232,160,32,0.08)';
  const labelColor = 'var(--text-secondary)';
  const valueColor = isDark ? 'rgba(232,224,232,0.9)' : '#3A3A3A';
  return (
    <div
      className="fixed inset-0 z-[520] flex items-end sm:items-center justify-center px-0 sm:px-4"
      style={{ background: overlayBg }}
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[80dvh] sm:max-h-[85dvh]"
        style={{ background: sheetBg, border: `1px solid ${borderColor}` }}
        onClick={event => event.stopPropagation()}
      >
        {/* 拖拽把手（仅移动端） */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: isDark ? 'rgba(183,154,197,0.35)' : 'rgba(232,160,32,0.28)' }} />
        </div>
        {/* header */}
        <div className="px-4 py-3 flex items-center justify-between border-b flex-shrink-0" style={{ borderColor }}>
          <div className="min-w-0 flex items-center gap-2">
            <span
              className="text-[0.55rem] px-2 py-0.5 rounded-full font-bold flex-shrink-0"
              style={{ background: item.bg, color: item.color, border: `1px solid ${item.border}` }}
            >
              {getShenshaLevelLabel(item.level, i18n.language)}
            </span>
            <p className="text-base font-bold" style={{ color: item.color, fontFamily: "'Noto Serif TC', serif" }}>{item.name}</p>
            {item.pillars.length > 0 && (
              <p className="text-[10px] truncate" style={{ color: labelColor }}>{item.pillars.join(' · ')}</p>
            )}
          </div>
          <button
            type="button"
            className="w-8 h-8 rounded-full flex items-center justify-center active:opacity-70 flex-shrink-0"
            style={{ background: sectionBg }}
            onClick={onClose}
          >
            <i className="ri-close-line text-base" style={{ color: labelColor }} />
          </button>
        </div>
        {/* scrollable body */}
        <div className="px-4 py-4 space-y-3 overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          {/* context chip */}
          <p className="text-[10px]" style={{ color: labelColor }}>{context}</p>

          {/* main card — shensha card style */}
          <div
            className="rounded-2xl overflow-hidden border"
            style={{ borderColor: item.border, background: item.bg }}
          >
            <div className="px-4 py-3">
              {/* description */}
              <p className="text-[0.65rem] leading-relaxed" style={{ color: valueColor }}>
                {item.desc}
              </p>
              {/* resolve hint — matches the lightbulb row in the basic chart */}
              {item.resolve && (
                <div
                  className="flex items-start gap-1.5 mt-2.5 px-2.5 py-2 rounded-xl"
                  style={{ background: isDark ? 'rgba(183,154,197,0.10)' : 'rgba(232,160,32,0.08)' }}
                >
                  <i className="ri-lightbulb-line text-xs flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-secondary)' }} />
                  <p className="text-[0.6rem] leading-relaxed" style={{ color: labelColor }}>
                    {item.resolve}
                  </p>
                </div>
              )}
            </div>
          </div>

          <RiskNotice isDark={isDark} />

          {/* 影响力 */}
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[10px]" style={{ color: labelColor }}>
              {i18n.language.startsWith('zh') ? '影响力' : i18n.language === 'en' ? 'Influence' : i18n.language === 'ko' ? '영향력' : '影響力'}
            </span>
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(183,154,197,0.15)' : 'rgba(232,160,32,0.12)' }}>
              <div className="h-full rounded-full" style={{ width: `${item.influenceScore}%`, background: item.color }}></div>
            </div>
            <span className="text-[10px] font-bold" style={{ color: item.color }}>{item.influenceScore}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function ProfessionalPaipan(props: Props) {
  const { t, i18n } = useTranslation('paipan');
  const [activeModal, setActiveModal] = useState<'rizhu' | 'wangshuai' | 'yinyang' | 'geju' | 'xiyong' | 'jishen' | null>(null);
  const [selectedDayunIndex, setSelectedDayunIndex] = useState<number | null>(null);
  const currentYear = props.currentYear ?? new Date().getFullYear();
  const [selectedLiunianYear, setSelectedLiunianYear] = useState<number>(() => (
    resolveInitialTransitYear(props.daYun, props.liuNian, currentYear)
  ));
  const [hasSelectedLiuyue, setHasSelectedLiuyue] = useState(false);
  const [hasSelectedLiuri, setHasSelectedLiuri] = useState(false);
  const [selectedLiuyueIndex, setSelectedLiuyueIndex] = useState(0);
  const [selectedLiuriIndex, setSelectedLiuriIndex] = useState(0);
  const [selectedShensha, setSelectedShensha] = useState<SelectedShensha | null>(null);
  const [showYueling, setShowYueling] = useState(true);
  const [expandedYueling, setExpandedYueling] = useState(false);
  const [showXiaoyunInDayun, setShowXiaoyunInDayun] = useState(false);
  const [showQiYunInfo, setShowQiYunInfo] = useState(false);
  const [showLiuyue, setShowLiuyue] = useState(true);
  const [showDayunLiunian, setShowDayunLiunian] = useState(true);
  const [showTransitLinkRules, setShowTransitLinkRules] = useState(false);
  const [selectedJieQi, setSelectedJieQi] = useState<number | null>(null);
  const [showJieQiModal, setShowJieQiModal] = useState(false);

  const isDark = props.isDark ?? true;
  const lang = i18n.language;
  const isZh = lang.startsWith('zh');
  const trd = (text: string) => convertText(text, lang);
  const birthTime = props.birthTime || '';
  const birthPlace = props.birthPlace || '';
  const mainPillars = props.pillars.slice(0, 4);
  const pillarsOk = mainPillars.length >= 4;
  const dayStem = pillarsOk ? mainPillars[2].tianGan : '';
  const dayStemEl = STEM_ELEMENT[dayStem as Stem] || '';
  // 选中流年 → 派生 12 节令月（年表）。纯历法，不依赖出生地/时区。四柱不足（无日主）时不计算。
  const yearFlow = useMemo<FlowCalendarResult | null>(() => {
    if (!pillarsOk || !dayStem || !selectedLiunianYear) return null;
    try {
      return computeFlowCalendar({ year: selectedLiunianYear, dayStem: dayStem as Stem });
    } catch {
      return null;
    }
  }, [pillarsOk, dayStem, selectedLiunianYear]);

  const yinYangRef = useMemo(() => calcYinYangRef(props.fourPillars, props.gender), [props.fourPillars, props.gender]);

  const professionalData = useMemo(() => {
    try {
      const fp = props.fourPillars || calcFourPillars(birthTime || '1990-01-01 12:00', birthPlace || '');
      const filterConfiguredShensha = (items: ShenshaItem[]) => props.shenshaDisplay === 'none'
        ? []
        : props.shenshaDisplay === 'common'
          ? sortShensha(items).slice(0, 12)
          : items;
      // 空数组代表用户明确关闭神煞 / 服务端明确无神煞，不能再因 falsy 回退到重新计算。
      const natal = sortShensha(filterConfiguredShensha(props.tableShenshaItems ?? props.shenshaItems ?? calcShensha(fp, 'zh-CN')));
      const natalGroups = groupNatalShenshaByPillar(natal);

      const dayunList: TransitShensha[] = props.daYun.map(dy => {
        const tg = resolveTransitTenGods(dy);
        return {
          label: `${dy.startYear} · ${dy.ageRange}`,
          startYear: dy.startYear,
          endYear: dy.endYear,
          stem: dy.tianGan,
          branch: dy.diZhi,
          stemShishen: tg.stem,
          branchShishen: tg.branch,
          isActive: !!dy.isActive,
          items: filterConfiguredShensha(resolveTransitShenshaItems(dy.shenSha, calcTransitShensha(fp, dy.tianGan, dy.diZhi, lang))),
        };
      });

      const liunianByYear = new Map<number, TransitShensha>();
      props.liuNian.forEach(ln => {
        const tg = resolveTransitTenGods(ln);
        liunianByYear.set(ln.year, {
          label: `${ln.year}`,
          year: ln.year,
          stem: ln.tianGan,
          branch: ln.diZhi,
          stemShishen: tg.stem,
          branchShishen: tg.branch,
          isActive: ln.year === currentYear,
          items: filterConfiguredShensha(resolveTransitShenshaItems(ln.shenSha, calcTransitShensha(fp, ln.tianGan, ln.diZhi, lang))),
        });
      });

      const defaultDayunIndex = Math.max(0, dayunList.findIndex(item => item.isActive));
      return { natal, natalGroups, dayunList, liunianByYear, defaultDayunIndex };
    } catch {
      return null;
    }
  }, [props.fourPillars, props.tableShenshaItems, props.shenshaItems, props.shenshaDisplay, birthTime, birthPlace, lang, props.daYun, props.liuNian, currentYear]);

  const activeDayunIndex = professionalData
    ? Math.min(selectedDayunIndex ?? professionalData.defaultDayunIndex, professionalData.dayunList.length - 1)
    : -1;
  const activeDayun = professionalData && activeDayunIndex >= 0 ? professionalData.dayunList[activeDayunIndex] : undefined;
  const activeDayunYears = useMemo(() => {
    if (!activeDayun) return [];
    return getScopedLiunianList(props.liuNian, { startYear: activeDayun.startYear, endYear: activeDayun.endYear });
  }, [props.liuNian, activeDayun]);

  // 点大运夹回 / 大运变化自动校正流年到范围内默认年
  const handleDayunClick = (index: number) => {
    const next = resolveDayunClickSelection(props.daYun, props.liuNian, index, currentYear);
    setSelectedDayunIndex(next.selectedDayunIndex);
    setSelectedLiunianYear(next.selectedLiunianYear);
    setSelectedLiuyueIndex(0);
    setSelectedLiuriIndex(0);
    setHasSelectedLiuyue(false);
    setHasSelectedLiuri(false);
  };
  const dayunStartYear = activeDayun?.startYear;
  const dayunEndYear = activeDayun?.endYear;
  useEffect(() => {
    if (dayunStartYear === undefined || dayunEndYear === undefined) return;
    const years = activeDayunYears.map(ln => ln.year).filter((year): year is number => typeof year === 'number');
    if (years.includes(selectedLiunianYear)) return;
    const next = resolveDefaultLiunianYear(years, currentYear);
    if (next !== selectedLiunianYear) setSelectedLiunianYear(next);
  }, [dayunStartYear, dayunEndYear, activeDayunYears, selectedLiunianYear, currentYear]);

  const selectedLiunian = useMemo(() => props.liuNian.find(ln => ln.year === selectedLiunianYear), [props.liuNian, selectedLiunianYear]);
  const activeLiunian = professionalData?.liunianByYear.get(selectedLiunianYear);

  // 切流年：门控复位（流月/流日钻取折叠，需重新点击展开）
  useEffect(() => {
    setHasSelectedLiuyue(false);
    setHasSelectedLiuri(false);
    setSelectedLiuyueIndex(0);
    setSelectedLiuriIndex(0);
  }, [selectedLiunianYear]);

  const liuyueRows = yearFlow?.liuYue ?? [];
  // 当年自动选中当前节令月（仅当前年）；其余年默认第一个。未点流月不派生流日。
  useEffect(() => {
    if (liuyueRows.length === 0) return;
    const now = new Date();
    const idx = selectedLiunianYear === currentYear ? resolveFlowMonthIndex(liuyueRows, now) : -1;
    setSelectedLiuyueIndex(idx >= 0 ? idx : 0);
    setSelectedLiuriIndex(0);
  }, [selectedLiunianYear, currentYear, liuyueRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedFlowMonth = liuyueRows[selectedLiuyueIndex];
  const selectedFlowMonthIndex = selectedFlowMonth?.monthIndex;
  const handleFlowMonthClick = (index: number) => {
    setSelectedLiuyueIndex(index);
    setSelectedLiuriIndex(0);
    setHasSelectedLiuyue(true);
    setHasSelectedLiuri(false);
  };
  // 点流月 → 派生该节令月逐日（monthFlow）。四柱不足时不计算。
  const monthFlow = useMemo<FlowCalendarResult | null>(() => {
    if (!pillarsOk || !hasSelectedLiuyue || !selectedFlowMonthIndex) return null;
    try {
      return computeFlowCalendar({ year: selectedLiunianYear, dayStem: dayStem as Stem, monthIndex: selectedFlowMonthIndex });
    } catch {
      return null;
    }
  }, [pillarsOk, hasSelectedLiuyue, selectedFlowMonthIndex, selectedLiunianYear, dayStem]);

  // 流日「今天」自动高亮：流月选中且该月逐日（days）到位后，所选流年为当年 → 命中今天，否则 0（对齐原型 L908-915）。
  // 数据由 monthFlow 派生（同帧到位）；切流月/换流年后 monthFlow 身份变化会再补一次 today 定位。
  // 用户已手动选中某流日（hasSelectedLiuri）后不再覆盖其选择（月份/年份切换已由别处复位 hasSelectedLiuri）。
  useEffect(() => {
    if (!hasSelectedLiuyue || !monthFlow || !monthFlow.days || monthFlow.days.length === 0) return;
    if (hasSelectedLiuri) return;
    setSelectedLiuriIndex(resolveFlowDayIndex(monthFlow.days, new Date(), selectedLiunianYear));
  }, [hasSelectedLiuyue, hasSelectedLiuri, selectedLiunianYear, monthFlow]);

  const liuriRows = monthFlow?.days ?? [];
  const selectedFlowDay = hasSelectedLiuri ? liuriRows[selectedLiuriIndex] ?? liuriRows[0] : undefined;
  const selectedFlowDayDate = selectedFlowDay?.date;
  const handleFlowDayClick = (index: number) => {
    setSelectedLiuriIndex(index);
    setHasSelectedLiuri(true);
  };
  // 点流日 → 派生该日 12 双小时（dayFlow）。四柱不足时不计算。
  const dayFlow = useMemo<FlowCalendarResult | null>(() => {
    if (!pillarsOk || !hasSelectedLiuri || !selectedFlowDayDate || !selectedFlowMonthIndex) return null;
    try {
      return computeFlowCalendar({
        year: selectedLiunianYear,
        dayStem: dayStem as Stem,
        monthIndex: selectedFlowMonthIndex,
        day: selectedFlowDayDate,
      });
    } catch {
      return null;
    }
  }, [pillarsOk, hasSelectedLiuri, selectedFlowDayDate, selectedFlowMonthIndex, selectedLiunianYear, dayStem]);

  const liushiRows = dayFlow?.hours ?? [];

  const tongXianData = useMemo(() => {
    const birthYear = parseInt(birthTime.split('-')[0], 10);
    const startAgeVal = props.startAge ?? 0;
    const endAge = startAgeVal > 1 ? startAgeVal - 1 : 0;
    return {
      year: birthYear,
      ageRange: endAge > 0 ? `1~${endAge}岁` : '童限',
      ageRange_en: endAge > 0 ? `1~${endAge} yrs` : 'Childhood',
    };
  }, [birthTime, props.startAge]);

  // 小运矩阵分组：童限组（出生年..首运前）+ 每步大运各一组（对齐原型 dayunGroups）。
  const dayunGroups = useMemo(() => {
    const groups: { startYear: number; endYear: number; years: LiuNian[] }[] = [];
    if (tongXianData.year > 0 && props.daYun.length > 0) {
      const firstDy = props.daYun[0];
      const tongXianYears = props.liuNian.filter(ln => ln.year >= tongXianData.year && ln.year < firstDy.startYear);
      if (tongXianYears.length > 0) {
        groups.push({ startYear: tongXianData.year, endYear: firstDy.startYear - 1, years: tongXianYears });
      }
    }
    props.daYun.forEach(dy => {
      const years = props.liuNian.filter(ln => ln.year >= dy.startYear && ln.year <= dy.endYear);
      groups.push({ startYear: dy.startYear, endYear: dy.endYear, years });
    });
    return groups;
  }, [props.liuNian, props.daYun, tongXianData.year]);

  const mingBranch = mainPillars[1]?.diZhi || '';
  const yuelingData = useMemo((): YuelingResult | null => {
    if (props.yuelingData !== undefined) return props.yuelingData;
    try {
      const fp = props.fourPillars || calcFourPillars(birthTime || '1990-01-01 12:00', birthPlace || '');
      return calcYueling(fp, birthTime || '1990-01-01 12:00');
    } catch {
      return null;
    }
  }, [props.yuelingData, props.fourPillars, birthTime, birthPlace]);
  const xiaoyunItems = useMemo((): XiaoyunItem[] => {
    if (props.xiaoyunItems) return props.xiaoyunItems;
    try {
      const fp = props.fourPillars || calcFourPillars(birthTime || '1990-01-01 12:00', birthPlace || '');
      const [datePart] = (birthTime || '1990-01-01 12:00').split(' ');
      const [by] = datePart.split('-').map(Number);
      return calcXiaoyun(fp, by, props.startAge || 0, props.isForward || true, currentYear, 20, lang);
    } catch {
      return [];
    }
  }, [props.xiaoyunItems, props.fourPillars, birthTime, birthPlace, props.startAge, props.isForward, currentYear, lang]);
  const currentXiaoyun = useMemo(() => xiaoyunItems.find(it => it.isActive), [xiaoyunItems]);

  const cardBg = isDark ? 'var(--bg-overlay-light)' : 'rgba(245,245,247,0.9)';
  const cardBorder = 'rgba(var(--accent-primary-rgb),0.2)';
  const rowBorder = isDark ? 'rgba(183,154,197,0.12)' : 'rgba(232,160,32,0.12)';
  const rowBorderLight = isDark ? 'rgba(183,154,197,0.1)' : 'rgba(232,160,32,0.1)';
  const labelColor = 'var(--text-secondary)';
  const valueColor = 'var(--text-primary)';
  const accentColor = 'var(--accent-primary)';
  const goldColor = 'var(--accent-secondary)';
  const transitCardBg = isDark ? cardBg : 'rgba(246,247,248,0.88)';
  const transitCardBorder = isDark ? cardBorder : 'rgba(80,86,94,0.22)';
  const transitLabelColor = isDark ? labelColor : '#5f6368';
  const transitValueColor = isDark ? valueColor : '#303236';
  const transitAccentColor = isDark ? accentColor : '#45484d';
  const sectionBg = isDark ? 'var(--bg-overlay-mid)' : 'rgba(245,245,247,0.8)';
  const sectionBorder = 'rgba(var(--accent-primary-rgb), 0.15)';

  const tStem = (stem: string): string => {
    if (isZh || !stem) return stem;
    return getStemLabel(stem, lang);
  };
  const tBranch = (branch: string): string => {
    if (isZh || !branch) return branch;
    return getBranchLabel(branch, lang);
  };
  const tBazi = (term: string): string => {
    if (isZh) return term;
    return t(`bazi.${term}`, { defaultValue: term }) as string;
  };
  const translateAge = (ageRange: string, ageRange_en?: string): string => {
    if (isZh) return trd(ageRange);
    if (ageRange_en) return ageRange_en;
    if (lang === 'ja') return ageRange.replace('岁', '歳');
    if (lang === 'ko') return ageRange.replace('岁', '세');
    return ageRange.replace('岁', ' yrs');
  };
  const tYearUnit = (year: number) => (isZh ? `${year}${trd('年')}` : lang === 'ja' ? `${year}年` : lang === 'ko' ? `${year}년` : `${year}`);

  // 起运折叠摘要：先用稳健 util（qiYunInfo → 出生/交运日期实算）；拿不到才回退现行岁文案
  const ageFallbackLabel = (() => {
    const age = props.startAge ?? 0;
    const months = props.startMonths && props.startMonths > 0 ? props.startMonths : 0;
    if (lang === 'en') return `Age ${age}${months > 0 ? ` ${months}mo` : ''}`;
    if (lang === 'ko') return `${age}세${months > 0 ? ` ${months}개월` : ''} 시작`;
    if (lang === 'ja') return `${age}歳${months > 0 ? ` ${months}ヶ月` : ''}開始`;
    return trd(`${age}岁${months > 0 ? `-${months}个月` : ''}起运`);
  })();
  const qiyunCollapsedLabel = (() => {
    const label = resolveQiyunCollapsedLabel({
      qiYunInfo: props.qiYunInfo,
      birthTime: props.birthTime,
      jiaoYunInfo: props.jiaoYunInfo,
      lang,
    });
    if (!label) return ageFallbackLabel;
    if (lang === 'en' || lang === 'ko' || lang === 'ja') return label;
    return trd(label);
  })();

  const shenshaTagItems = (items?: ShenshaItem[], limit = 6) => sortShensha(items ?? []).slice(0, limit);
  const getShenshaTagTone = (level: ShenshaItem['level']) => {
    if (level === '大吉' || level === '吉') {
      return {
        color: isDark ? '#7BC67E' : '#63b66f',
        bg: 'rgba(123,198,126,0.12)',
        border: isDark ? 'rgba(123,198,126,0.48)' : 'rgba(99,182,111,0.42)',
      };
    }
    if (level === '注意' || level === '化解') {
      return {
        color: isDark ? labelColor : '#4f5358',
        bg: isDark ? 'rgba(183,154,197,0.10)' : 'rgba(93,99,107,0.08)',
        border: isDark ? 'rgba(183,154,197,0.25)' : 'rgba(93,99,107,0.24)',
      };
    }
    return {
      color: isDark ? goldColor : '#4f5358',
      bg: isDark ? 'rgba(232,195,158,0.10)' : 'rgba(93,99,107,0.08)',
      border: isDark ? 'rgba(232,195,158,0.34)' : 'rgba(93,99,107,0.24)',
    };
  };
  const renderShenshaTags = (items: ShenshaItem[], context: string) => (
    <div className="flex flex-wrap gap-2 mt-3">
      {items.length > 0 ? items.map((item, idx) => {
        const tone = getShenshaTagTone(item.level);
        return (
          <button
            type="button"
            key={`${context}-${item.name}-${idx}`}
            onClick={() => setSelectedShensha({ item, context })}
            className="bazi-transit-tag px-2.5 py-1 rounded-full font-bold active:scale-[0.98] transition-transform"
            style={{ color: tone.color, background: tone.bg, border: `1px solid ${tone.border}` }}
          >
            {tBazi(item.name)} <span className="opacity-80">{getShenshaLevelLabel(item.level, i18n.language)}</span>
          </button>
        );
      }) : (
        <span className="bazi-transit-meta" style={{ color: transitLabelColor }}>{isZh ? trd('暂无重点神煞') : 'No highlighted stars'}</span>
      )}
    </div>
  );
  const getBranchTransitRelation = (dayunBranch?: string, liunianBranch?: string) => {
    if (!dayunBranch || !liunianBranch) return isZh ? trd('流年与大运需合参') : 'Transit needs cross reading';
    const pair = `${dayunBranch}${liunianBranch}`;
    const reversed = `${liunianBranch}${dayunBranch}`;
    const hasPair = (pairs: string[]) => pairs.includes(pair) || pairs.includes(reversed);
    if (dayunBranch === liunianBranch) return isZh ? trd(`流年${liunianBranch}与大运${dayunBranch}同气叠见`) : 'Annual branch repeats the decade branch';
    if (hasPair(['子午', '丑未', '寅申', '卯酉', '辰戌', '巳亥'])) return isZh ? trd(`流年${liunianBranch}冲大运${dayunBranch}`) : 'Annual branch clashes with the decade branch';
    if (hasPair(['子丑', '寅亥', '卯戌', '辰酉', '巳申', '午未'])) return isZh ? trd(`流年${liunianBranch}合大运${dayunBranch}`) : 'Annual branch combines with the decade branch';
    if (hasPair(['子未', '丑午', '寅巳', '卯辰', '申亥', '酉戌'])) return isZh ? trd(`流年${liunianBranch}害大运${dayunBranch}`) : 'Annual branch harms the decade branch';
    if (hasPair(['子酉', '卯午', '辰丑', '未戌', '寅亥', '巳申'])) return isZh ? trd(`流年${liunianBranch}破大运${dayunBranch}`) : 'Annual branch breaks the decade branch';
    return isZh ? trd(`流年${liunianBranch}与大运${dayunBranch}以五行生克合参`) : 'Annual and decade branches are read by element interaction';
  };
  const transitLinkRules = (dayun?: TransitShensha, liunian?: TransitShensha) => {
    const dayunItems = shenshaTagItems(dayun?.items, 8);
    const liunianItems = shenshaTagItems(liunian?.items, 8);
    const repeated = liunianItems.filter(item => dayunItems.some(dy => dy.name === item.name));
    const focus = (repeated.length > 0 ? repeated : liunianItems).slice(0, 2).map(item => tBazi(item.name)).join('、') || (isZh ? trd('流年主星') : 'annual stars');
    const relation = getBranchTransitRelation(dayun?.branch, liunian?.branch);
    if (!isZh) {
      return [
        repeated.length > 0 ? `${focus} appears in both decade and annual layers, so the theme is more likely to be triggered this year.` : 'Read the decade luck as the background and the annual luck as the yearly trigger.',
        `${relation}; watch movement, plans, relationships, and timing before making forceful decisions.`,
        `This year focuses on ${focus}. When it repeats with natal or decade signs, treat it as an external trigger of the natal chart.`,
      ];
    }
    return [
      repeated.length > 0
        ? trd(`同星见：${focus}同时叠起大运与流年，主类象在本年更容易被触发。`)
        : trd('先看大运后看流年：大运定十年背景，流年定当年触发点，不能只凭一年干支单断。'),
      trd(`${relation}，主震动、行走、关系或计划调整；遇到重点神煞时，宜先停一步观察再处理。`),
      trd(`流年需重点关注：${focus}。若与原局或大运重复，应按“外应触发原局”处理。`),
    ];
  };

  const getStemDisplayColor = (stem?: string) => stem && STEM_ELEMENT[stem as Stem] ? ELEMENT_COLORS[STEM_ELEMENT[stem as Stem]] : valueColor;
  const getBranchDisplayColor = (branch?: string) => BRANCH_FIRST_WUXING[branch ?? '']?.color || goldColor;
  const getWeekdayLabel = (date: Date) => {
    if (lang === 'en') return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
    if (lang === 'ko') return ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'][date.getDay()];
    if (lang === 'ja') return ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'][date.getDay()];
    return trd(['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'][date.getDay()]);
  };
  const formatSelectedDate = (date: Date) => {
    const y = date.getFullYear();
    const mo = date.getMonth() + 1;
    const d = date.getDate();
    if (isZh) return trd(`${y}年${mo}月${d}日`);
    if (lang === 'ko') return `${y}년 ${mo}월 ${d}일`;
    if (lang === 'ja') return `${y}年${mo}月${d}日`;
    return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  return (
    <div>
      <div className="px-0 pt-2 pb-3 md:px-3 md:pt-4 md:pb-6">
        {!professionalData ? (
          <p className="text-[10px] text-center py-5" style={{ color: labelColor }}>{t('professionalPaipan.calculating')}</p>
        ) : (
          <div className="space-y-2 md:space-y-4">
            <ProfessionalFourPillarGrid
              pillars={props.pillars}
              natalGroups={professionalData.natalGroups}
              activeLiunian={activeLiunian}
              activeDayun={activeDayun}
              mingGong={props.mingGong}
              isDark={isDark}
              rowBorder={rowBorder}
              labelColor={labelColor}
              valueColor={valueColor}
              onShenshaClick={(item, context) => setSelectedShensha({ item, context })}
            >

                {activeDayun && activeLiunian && (
                  <div style={{ background: 'transparent' }}>
                    {/* 折叠头部：大运流年 */}
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={showDayunLiunian}
                      className="flex items-center justify-between cursor-pointer active:opacity-80 transition-opacity py-2"
                      onClick={() => setShowDayunLiunian(v => !v)}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setShowDayunLiunian(v => !v); }}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-1 h-3 rounded-full flex-shrink-0" style={{ background: accentColor }}></div>
                        <span className="text-xs font-bold truncate" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>
                          {isZh ? trd('大运流年') : 'Dayun Liunian'}
                          <span className="ml-1.5" style={{ color: isDark ? '#E8C39E' : '#C07010', fontWeight: 500 }}>
                            {tStem(activeDayun.stem)}{tBranch(activeDayun.branch)} · {tStem(activeLiunian.stem)}{tBranch(activeLiunian.branch)}
                          </span>
                        </span>
                      </div>
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`${showDayunLiunian ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} style={{ color: labelColor }}></i>
                      </div>
                    </div>

                    {showDayunLiunian && (
                      <div className="border-t" style={{ borderColor: rowBorderLight }}>
                        <div className="px-2 py-3 space-y-3">

                          {/* 起运信息 */}
                          <div className="px-2 py-2" style={{ background: isDark ? 'rgba(107,78,113,0.06)' : 'rgba(255,248,236,0.5)' }}>
                            <div
                              role="button"
                              tabIndex={0}
                              aria-expanded={showQiYunInfo}
                              className="px-2 py-2 flex items-center justify-between gap-2 cursor-pointer active:opacity-80 transition-opacity"
                              style={{ borderBottom: showQiYunInfo ? `1px solid ${rowBorderLight}` : 'none' }}
                              onClick={() => setShowQiYunInfo(v => !v)}
                              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setShowQiYunInfo(v => !v); }}
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <div className="w-1 h-3 rounded-full flex-shrink-0" style={{ background: goldColor }}></div>
                                <span className="text-xs font-bold flex-shrink-0" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>
                                  {isZh ? trd('起运信息') : lang === 'en' ? 'Fortune Start' : lang === 'ko' ? '대운 시작 정보' : '大運開始情報'}
                                </span>
                                {!showQiYunInfo && (
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-xs font-bold truncate" style={{ color: valueColor }}>
                                      {qiyunCollapsedLabel}
                                    </span>
                                    {props.isForward !== undefined && (
                                      <span
                                        className="text-[0.5rem] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
                                        style={{
                                          background: props.isForward
                                            ? (isDark ? 'rgba(123,198,126,0.2)' : 'rgba(123,198,126,0.15)')
                                            : (isDark ? 'rgba(209,75,69,0.2)' : 'rgba(209,75,69,0.15)'),
                                          color: props.isForward ? '#7BC67E' : '#D14B45',
                                          border: `1px solid ${props.isForward ? '#7BC67E' : '#D14B45'}40`,
                                        }}
                                      >
                                        {isZh
                                          ? (props.isForward ? trd('順排') : trd('逆排'))
                                          : lang === 'en' ? (props.isForward ? 'Forward' : 'Reverse')
                                          : lang === 'ko' ? (props.isForward ? '순행' : '역행')
                                          : (props.isForward ? '順行' : '逆行')
                                        }
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                <i className={`${showQiYunInfo ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} style={{ color: labelColor }}></i>
                              </div>
                            </div>

                            {showQiYunInfo && (<>
                              {/* 精确起运时间——核心展示区 */}
                              {props.preciseJiaoYear && props.preciseJiaoMonth && (
                                <div className="px-2 md:px-3 py-3 flex items-center gap-3" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                                  <div
                                    className="flex-shrink-0 flex flex-col items-center justify-center rounded-xl px-3 py-2"
                                    style={{ background: isDark ? `${goldColor}15` : `${goldColor}12`, border: `1px solid ${goldColor}40`, minWidth: 64 }}
                                  >
                                    <span className="text-[9px] font-medium" style={{ color: goldColor, opacity: 0.8 }}>
                                      {props.preciseJiaoYear}
                                    </span>
                                    <span className="text-2xl font-bold leading-tight" style={{ color: goldColor, fontFamily: "'Noto Serif TC', serif" }}>
                                      {String(props.preciseJiaoMonth).padStart(2, '0')}
                                    </span>
                                    <span className="text-[9px]" style={{ color: goldColor, opacity: 0.7 }}>
                                      {isZh ? trd('月') : lang === 'ja' ? '月' : lang === 'ko' ? '월' : 'Month'}
                                    </span>
                                  </div>

                                  <div className="flex-1 min-w-0 space-y-1.5">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-xs font-bold" style={{ color: valueColor }}>{ageFallbackLabel}</span>
                                      {props.isForward !== undefined && (
                                        <span
                                          className="text-[0.5rem] px-1.5 py-0.5 rounded-full font-bold"
                                          style={{
                                            background: props.isForward
                                              ? (isDark ? 'rgba(123,198,126,0.2)' : 'rgba(123,198,126,0.15)')
                                              : (isDark ? 'rgba(209,75,69,0.2)' : 'rgba(209,75,69,0.15)'),
                                            color: props.isForward ? '#7BC67E' : '#D14B45',
                                            border: `1px solid ${props.isForward ? '#7BC67E' : '#D14B45'}40`,
                                          }}
                                        >
                                          {isZh
                                            ? (props.isForward ? trd('順排') : trd('逆排'))
                                            : lang === 'en' ? (props.isForward ? 'Forward' : 'Reverse')
                                            : lang === 'ko' ? (props.isForward ? '순행' : '역행')
                                            : (props.isForward ? '順行' : '逆行')
                                          }
                                        </span>
                                      )}
                                    </div>

                                    {props.qiYunInfo && (
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] font-medium" style={{ color: accentColor }}>
                                          {trd(formatQiYunOffset(props.qiYunInfo))}
                                        </span>
                                      </div>
                                    )}

                                    <div className="text-[10px] leading-relaxed" style={{ color: isDark ? 'rgba(232,224,232,0.75)' : '#5A4A35' }}>
                                      {props.jiaoYunInfo.split('\n').map((line, i) => (
                                        <p key={i} className={i > 0 ? 'mt-0.5' : ''}>
                                          {i > 0 ? (
                                            <span style={{ color: isDark ? '#E8C39E' : '#3a3a3c', fontWeight: 500 }}>{trd(line)}</span>
                                          ) : trd(line)}
                                        </p>
                                      ))}
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <div className="flex items-center gap-1">
                                        <span className="text-[9px]" style={{ color: labelColor }}>
                                          {isZh ? trd('当前') : lang === 'en' ? 'Now' : lang === 'ko' ? '현재' : '現在'}
                                        </span>
                                        <span className="text-[10px] font-bold" style={{ color: goldColor }}>
                                          {props.currentAge}{lang === 'ja' ? '歳' : lang === 'ko' ? '세' : lang === 'en' ? ' yrs' : trd('岁')}
                                        </span>
                                      </div>
                                      <div className="w-px h-3" style={{ background: rowBorderLight }}></div>
                                      <div className="flex items-center gap-1">
                                        <span className="text-[9px]" style={{ color: labelColor }}>
                                          {isZh ? trd('司令') : lang === 'en' ? 'Ruler' : lang === 'ko' ? '사령' : '司令'}
                                        </span>
                                        <span className="text-[10px] font-bold" style={{ color: accentColor }}>{trd(props.siLing)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="px-2 py-2 flex items-center gap-1.5">
                                <i className="ri-information-line text-xs flex-shrink-0" style={{ color: accentColor, opacity: 0.7 }}></i>
                                <p className="text-[9px] leading-relaxed" style={{ color: labelColor }}>{trd(props.qiYunInfo)}</p>
                              </div>
                            </>)}
                          </div>

                          {/* 大运 */}
                          <div className="rounded-2xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                            <div className="px-3 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                              <div className="w-1 h-4 rounded-full" style={{ background: accentColor }}></div>
                              <span className="text-base font-bold" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>{isZh ? trd('大运') : lang === 'en' ? 'Major Luck' : lang === 'ko' ? '대운' : '大運'}</span>
                            </div>
                            <div className="overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                              <div className="flex" style={{ minWidth: 'max-content' }}>
                                {tongXianData.year > 0 && (
                                  <div className="flex flex-col items-center justify-center py-4 px-2 flex-shrink-0" style={{ width: 92, minHeight: 164, background: isDark ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.05)', borderRight: `1px solid ${rowBorderLight}` }}>
                                    <span className="text-[10px] mb-1" style={{ color: labelColor }}>{tongXianData.year}</span>
                                    <span className="text-[10px] font-bold mb-3" style={{ color: goldColor }}>{translateAge(tongXianData.ageRange, tongXianData.ageRange_en)}</span>
                                    <span className="text-xl font-bold leading-tight" style={{ color: labelColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit', writingMode: 'vertical-rl' }}>{trd('童限')}</span>
                                  </div>
                                )}
                                {props.daYun.map((dy, i) => {
                                  const isSelectedDayun = activeDayunIndex === i;
                                  const tg = resolveTransitTenGods(dy);
                                  const branchWuxing = resolveTransitBranchDisplay(dy).wuxing;
                                  return (
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      key={i}
                                      onClick={() => handleDayunClick(i)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          handleDayunClick(i);
                                        }
                                      }}
                                      className="flex flex-col items-center justify-center py-4 px-2 flex-shrink-0 active:scale-[0.98] transition-all cursor-pointer"
                                      style={{
                                        width: 96,
                                        minHeight: 164,
                                        background: isSelectedDayun ? (isDark ? 'rgba(var(--accent-primary-rgb),0.14)' : 'rgba(232,160,32,0.10)') : dy.isActive ? (isDark ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.04)') : undefined,
                                        boxShadow: isSelectedDayun ? `inset 0 0 0 1px ${goldColor}` : undefined,
                                        borderRight: i < props.daYun.length - 1 ? `1px solid ${rowBorderLight}` : 'none',
                                      }}
                                    >
                                      <span className="text-xs mb-1" style={{ color: labelColor }}>{dy.startYear}</span>
                                      <span className="text-xs font-bold mb-3" style={{ color: isSelectedDayun || dy.isActive ? goldColor : labelColor }}>{translateAge(dy.ageRange, dy.ageRange_en)}</span>
                                      <span className="text-xl font-bold leading-none mb-1" style={{ color: dy.tianGanColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tStem(dy.tianGan)}</span>
                                      <span className="text-[10px] mb-3" style={{ color: labelColor }}>{tBazi(tg.stem)}</span>
                                      <span className="text-xl font-bold leading-none mb-1" style={{ color: dy.diZhiColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tBranch(dy.diZhi)}</span>
                                      <span className="text-xs" style={{ color: labelColor }}>{tg.branch !== undefined && tg.branch !== dy.diZhiShen ? `${tBazi(tg.branch)} · ` : ''}{branchWuxing}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="w-full px-3 py-3 flex items-center gap-3 active:opacity-80 cursor-pointer"
                              style={{ borderTop: `1px solid ${rowBorderLight}` }}
                              onClick={() => setShowXiaoyunInDayun(v => !v)}
                            >
                              <div className="relative flex-shrink-0" style={{ width: 40, height: 22, borderRadius: 11, background: showXiaoyunInDayun ? accentColor : (isDark ? 'rgba(183,154,197,0.35)' : 'rgba(232,160,32,0.25)') }}>
                                <div className="absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white shadow transition-all" style={{ left: showXiaoyunInDayun ? 20 : 2 }} />
                              </div>
                              <span className="text-sm font-bold" style={{ color: showXiaoyunInDayun ? accentColor : labelColor }}>{isZh ? trd('小运') : lang === 'en' ? 'Minor Luck' : lang === 'ko' ? '소운' : '小運'}</span>
                              {showXiaoyunInDayun && currentXiaoyun && (
                                <span className="text-xs ml-auto" style={{ color: labelColor }}>
                                  {tStem(currentXiaoyun.stem)}{tBranch(currentXiaoyun.branch)}
                                </span>
                              )}
                              <i className={`${showXiaoyunInDayun ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg ml-auto`} style={{ color: labelColor }}></i>
                            </button>

                            {/* 小运矩阵：童限组 + 每步大运各一组（始于/止于头尾列） */}
                            {showXiaoyunInDayun && dayunGroups.length > 0 && (
                              <div className="overflow-x-auto" style={{ scrollbarWidth: 'none', borderTop: `1px solid ${rowBorderLight}` }}>
                                <div style={{ minWidth: 'max-content' }}>
                                  <div className="flex border-b" style={{ borderColor: rowBorderLight }}>
                                    <div className="flex-shrink-0 py-1.5 flex items-center justify-center" style={{ width: 44, borderRight: `1px solid ${rowBorderLight}` }}>
                                      <span className="text-[9px]" style={{ color: goldColor }}>{isZh ? trd('始于') : 'From'}</span>
                                    </div>
                                    {dayunGroups.map((g, i) => (
                                      <div key={`hs-${g.startYear}-${i}`} className="flex-shrink-0 py-1.5 flex items-center justify-center" style={{ width: 52, borderRight: i < dayunGroups.length - 1 ? `1px solid ${rowBorderLight}` : 'none' }}>
                                        <span className="text-[9px] font-bold" style={{ color: goldColor }}>{g.startYear}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {Array.from({ length: Math.max(...dayunGroups.map(g => g.years.length), 1) }).map((_, rowIdx) => (
                                    <div className="flex border-b" key={`row-${rowIdx}`} style={{ borderColor: rowBorderLight, minHeight: 28 }}>
                                      <div className="flex-shrink-0 py-1 flex items-center justify-center" style={{ width: 44, borderRight: `1px solid ${rowBorderLight}` }} />
                                      {dayunGroups.map((g, colIdx) => {
                                        const yearData = g.years[rowIdx];
                                        return (
                                          <div key={`cell-${g.startYear}-${colIdx}-${rowIdx}`} className="flex-shrink-0 py-1 flex flex-col items-center justify-center" style={{ width: 52, borderRight: colIdx < dayunGroups.length - 1 ? `1px solid ${rowBorderLight}` : 'none' }}>
                                            {yearData ? (
                                              <span className="text-[11px] font-bold leading-none" style={{ fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>
                                                <span style={{ color: yearData.tianGanColor || getStemDisplayColor(yearData.tianGan) }}>{tStem(yearData.tianGan)}</span>
                                                <span style={{ color: yearData.diZhiColor || getBranchDisplayColor(yearData.diZhi) }}>{tBranch(yearData.diZhi)}</span>
                                              </span>
                                            ) : (
                                              <span className="text-[8px]" style={{ color: labelColor }} />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))}
                                  <div className="flex" style={{ borderColor: rowBorderLight }}>
                                    <div className="flex-shrink-0 py-1.5 flex items-center justify-center" style={{ width: 44, borderRight: `1px solid ${rowBorderLight}` }}>
                                      <span className="text-[9px]" style={{ color: goldColor }}>{isZh ? trd('止于') : 'To'}</span>
                                    </div>
                                    {dayunGroups.map((g, i) => (
                                      <div key={`he-${g.endYear}-${i}`} className="flex-shrink-0 py-1.5 flex items-center justify-center" style={{ width: 52, borderRight: i < dayunGroups.length - 1 ? `1px solid ${rowBorderLight}` : 'none' }}>
                                        <span className="text-[9px] font-bold" style={{ color: goldColor }}>{g.endYear}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* 流年（小运矩阵打开时隐藏） */}
                          {!showXiaoyunInDayun && (
                          <div className="rounded-2xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                            <div className="px-3 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                              <div className="w-1 h-4 rounded-full" style={{ background: goldColor }}></div>
                              <span className="text-base font-bold" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>{isZh ? trd('流年') : lang === 'en' ? 'Annual Luck' : lang === 'ko' ? '연운' : '流年'}</span>
                            </div>
                            <div className="overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                              <div className="flex" style={{ minWidth: 'max-content' }}>
                                <div className="flex-shrink-0 flex flex-col" style={{ width: 72, borderRight: `1px solid ${rowBorderLight}` }}>
                                  <div className="py-3 flex items-center justify-center" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                                    <span className="text-xs" style={{ color: labelColor }}>{isZh ? trd('年份') : lang === 'en' ? 'Year' : lang === 'ko' ? '연도' : '年'}</span>
                                  </div>
                                  <div className="flex-1 flex flex-col items-center justify-center gap-0.5 py-4" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                                    <span className="text-xs" style={{ color: labelColor }}>{t('basicPaipan.col.tianGan')}</span>
                                  </div>
                                  <div className="flex-1 flex flex-col items-center justify-center gap-0.5 py-4">
                                    <span className="text-xs" style={{ color: labelColor }}>{t('basicPaipan.col.diZhi')}</span>
                                  </div>
                                </div>
                                {activeDayunYears.map((ln, i) => {
                                  const isSelected = ln.year === selectedLiunianYear;
                                  const isCurrentYear = ln.year === currentYear;
                                  const branchWuxing = BRANCH_FIRST_WUXING[ln.diZhi]?.wuxing || '';
                                  const tg = resolveTransitTenGods(ln);
                                  const bd = resolveTransitBranchDisplay(ln);
                                  return (
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      key={ln.year}
                                      onClick={() => setSelectedLiunianYear(ln.year)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') setSelectedLiunianYear(ln.year);
                                      }}
                                      className="flex flex-col items-center flex-shrink-0 text-left active:scale-[0.98] transition-all cursor-pointer"
                                      style={{
                                        width: 92,
                                        borderRight: i < activeDayunYears.length - 1 ? `1px solid ${rowBorderLight}` : 'none',
                                        background: isSelected ? (isDark ? 'rgba(var(--accent-primary-rgb),0.12)' : 'rgba(232,160,32,0.10)') : undefined,
                                      }}
                                    >
                                      <div className="w-full py-3 flex items-center justify-center" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                                        <span className="text-xs font-bold" style={{ color: isSelected || isCurrentYear ? goldColor : labelColor }}>{ln.year}</span>
                                      </div>
                                      <div className="w-full flex-1 flex flex-col items-center justify-center gap-1 py-4" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                                        <span className="text-xl font-bold leading-none" style={{ color: ln.tianGanColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tStem(ln.tianGan)}</span>
                                        <span className="text-[10px]" style={{ color: labelColor }}>{tBazi(tg.stem)}</span>
                                      </div>
                                      <div className="w-full flex-1 flex flex-col items-center justify-center gap-1 py-4">
                                        <span className="text-xl font-bold leading-none" style={{ color: ln.diZhiColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tBranch(ln.diZhi)}</span>
                                        <span className="text-[10px]" style={{ color: labelColor }}>{tBazi(bd.shishen)} · {branchWuxing}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {selectedLiunian && (
                              <div style={{ borderTop: `1px solid ${rowBorderLight}` }}>
                                <div className="px-3 py-3 flex items-center gap-2 flex-wrap" style={{ background: isDark ? 'rgba(183,154,197,0.06)' : 'rgba(232,160,32,0.04)' }}>
                                  <span className="text-sm font-bold" style={{ color: accentColor }}>{tYearUnit(selectedLiunian.year)}</span>
                                  <span className="text-xl font-bold" style={{ color: selectedLiunian.tianGanColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tStem(selectedLiunian.tianGan)}</span>
                                  <span className="text-xl font-bold" style={{ color: selectedLiunian.diZhiColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tBranch(selectedLiunian.diZhi)}</span>
                                  <span className="text-xs" style={{ color: labelColor }}>
                                    {tBazi(resolveTransitTenGods(selectedLiunian).stem)} · {BRANCH_FIRST_WUXING[selectedLiunian.diZhi]?.wuxing || ''}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setShowLiuyue(v => !v)}
                                    className="ml-auto flex items-center gap-1 active:opacity-80"
                                  >
                                    <span className="text-xs" style={{ color: labelColor }}>
                                      {isZh ? trd('流月') : lang === 'en' ? 'Monthly' : lang === 'ko' ? '유월' : '流月'}
                                    </span>
                                    <i className={`${showLiuyue ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} style={{ color: labelColor }}></i>
                                  </button>
                                </div>
                                {showLiuyue && selectedLiunian && (
                                  <div className="overflow-x-auto px-3 pb-4" style={{ scrollbarWidth: 'none' }}>
                                    {liuyueRows.length > 0 ? (
                                      <div style={{ minWidth: 'max-content' }}>
                                        {/* 节令月名+日期 */}
                                        <div className="flex border-b" style={{ borderColor: rowBorderLight }}>
                                          <div className="flex-shrink-0 py-3 flex items-center justify-center" style={{ width: 58 }}>
                                            <span className="text-xs" style={{ color: labelColor }}>{isZh ? trd('节气') : 'Term'}</span>
                                          </div>
                                          {liuyueRows.map((ly, i) => (
                                            <button
                                              type="button"
                                              key={ly.monthIndex}
                                              onClick={() => handleFlowMonthClick(i)}
                                              className="flex-shrink-0 py-3 flex flex-col items-center justify-center gap-0.5 active:scale-[0.98] transition-all"
                                              style={{ width: 70, background: i === selectedLiuyueIndex ? (isDark ? 'rgba(var(--accent-primary-rgb),0.13)' : 'rgba(232,160,32,0.10)') : undefined }}
                                            >
                                              <span className="text-xs font-bold" style={{ color: valueColor }}>{trd(ly.termName)}</span>
                                              <span className="text-[10px]" style={{ color: labelColor }}>{formatFlowTermDate(ly.termStart)}</span>
                                            </button>
                                          ))}
                                        </div>
                                        {/* 流月天干 */}
                                        <div className="flex border-b" style={{ borderColor: rowBorderLight }}>
                                          <div className="flex-shrink-0 py-4 flex items-center justify-center" style={{ width: 58 }}>
                                            <span className="text-xs" style={{ color: labelColor }}>{t('basicPaipan.col.tianGan')}</span>
                                          </div>
                                          {liuyueRows.map((ly, i) => {
                                            const stemColor = getStemDisplayColor(ly.tianGan);
                                            return (
                                              <button
                                                type="button"
                                                key={`${ly.monthIndex}-s`}
                                                onClick={() => handleFlowMonthClick(i)}
                                                className="flex-shrink-0 py-4 flex flex-col items-center justify-center gap-1 active:scale-[0.98] transition-all"
                                                style={{ width: 70, background: i === selectedLiuyueIndex ? (isDark ? 'rgba(var(--accent-primary-rgb),0.13)' : 'rgba(232,160,32,0.10)') : undefined }}
                                              >
                                                <span className="text-xl font-bold leading-none" style={{ color: stemColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tStem(ly.tianGan)}</span>
                                                <span className="text-[10px]" style={{ color: labelColor }}>{tBazi(resolveTransitTenGods(ly).stem)}</span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                        {/* 流月地支 */}
                                        <div className="flex border-b" style={{ borderColor: rowBorderLight }}>
                                          <div className="flex-shrink-0 py-4 flex items-center justify-center" style={{ width: 58 }}>
                                            <span className="text-xs" style={{ color: labelColor }}>{t('basicPaipan.col.diZhi')}</span>
                                          </div>
                                          {liuyueRows.map((ly, i) => {
                                            const branchColor = getBranchDisplayColor(ly.diZhi);
                                            return (
                                              <button
                                                type="button"
                                                key={`${ly.monthIndex}-b`}
                                                onClick={() => handleFlowMonthClick(i)}
                                                className="flex-shrink-0 py-4 flex flex-col items-center justify-center gap-1 active:scale-[0.98] transition-all"
                                                style={{ width: 70, background: i === selectedLiuyueIndex ? (isDark ? 'rgba(var(--accent-primary-rgb),0.13)' : 'rgba(232,160,32,0.10)') : undefined }}
                                              >
                                                <span className="text-2xl font-bold leading-none" style={{ color: branchColor, fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tBranch(ly.diZhi)}</span>
                                                <span className="text-[10px]" style={{ color: labelColor }}>{BRANCH_FIRST_WUXING[ly.diZhi]?.wuxing || ''}</span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                        {/* 流日 */}
                                        {hasSelectedLiuyue && (
                                          liuriRows.length > 0 ? (
                                            <>
                                              <div className="flex border-b mt-4" style={{ borderColor: rowBorderLight }}>
                                                <div className="flex-shrink-0 py-4 flex items-center justify-center" style={{ width: 58 }}>
                                                  <span className="text-xs font-bold" style={{ color: accentColor }}>{isZh ? trd('流日') : 'Daily'}</span>
                                                </div>
                                                {liuriRows.map((ld, i) => (
                                                  <button
                                                    type="button"
                                                    key={ld.date}
                                                    onClick={() => handleFlowDayClick(i)}
                                                    className="flex-shrink-0 py-3 flex flex-col items-center justify-center gap-1 active:scale-[0.98] transition-all"
                                                    style={{ width: 70, background: i === selectedLiuriIndex ? (isDark ? 'rgba(var(--accent-primary-rgb),0.13)' : 'rgba(232,160,32,0.10)') : undefined }}
                                                  >
                                                    <span className="text-xs" style={{ color: labelColor }}>{parseFlowDate(ld.date)?.getDate() ?? ld.date.slice(8)}</span>
                                                    <span className="text-xl font-bold leading-none" style={{ color: getStemDisplayColor(ld.tianGan), fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tStem(ld.tianGan)}</span>
                                                    <span className="text-[10px]" style={{ color: labelColor }}>{tBazi(ld.tianGanShen || '')}</span>
                                                    <span className="text-xl font-bold leading-none" style={{ color: getBranchDisplayColor(ld.diZhi), fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tBranch(ld.diZhi)}</span>
                                                    <span className="text-[10px]" style={{ color: labelColor }}>{BRANCH_FIRST_WUXING[ld.diZhi]?.wuxing || ''}</span>
                                                  </button>
                                                ))}
                                              </div>
                                              {/* 流时 */}
                                              {hasSelectedLiuri && (
                                                liushiRows.length > 0 ? (
                                                  <>
                                                    <div className="flex border-b" style={{ borderColor: rowBorderLight }}>
                                                      <div className="flex-shrink-0 py-4 flex items-center justify-center" style={{ width: 58 }}>
                                                        <span className="text-xs font-bold" style={{ color: accentColor }}>{isZh ? trd('流时') : 'Hourly'}</span>
                                                      </div>
                                                      {liushiRows.map((lh, i) => (
                                                        <div key={`${lh.hourIndex}-${i}`} className="flex-shrink-0 py-3 flex flex-col items-center justify-center gap-1" style={{ width: 70 }}>
                                                          <span className="text-xs" style={{ color: labelColor }}>{lh.hourLabel}</span>
                                                          <span className="text-xl font-bold leading-none" style={{ color: getStemDisplayColor(lh.tianGan), fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tStem(lh.tianGan)}</span>
                                                          <span className="text-[10px]" style={{ color: labelColor }}>{tBazi(lh.tianGanShen || '')}</span>
                                                          <span className="text-xl font-bold leading-none" style={{ color: getBranchDisplayColor(lh.diZhi), fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>{tBranch(lh.diZhi)}</span>
                                                          <span className="text-[10px]" style={{ color: labelColor }}>{BRANCH_FIRST_WUXING[lh.diZhi]?.wuxing || ''}</span>
                                                        </div>
                                                      ))}
                                                    </div>
                                                    {selectedFlowDay && (() => {
                                                      const date = parseFlowDate(selectedFlowDay.date);
                                                      return date ? (
                                                        <div className="py-3 text-xs" style={{ color: labelColor }}>
                                                          {isZh ? trd('已选日期：') : 'Selected date: '}
                                                          <span style={{ color: valueColor }}>
                                                            {formatSelectedDate(date)} {getWeekdayLabel(date)} {tStem(selectedFlowDay.tianGan)}{tBranch(selectedFlowDay.diZhi)}{isZh ? trd('日') : ''}
                                                          </span>
                                                        </div>
                                                      ) : null;
                                                    })()}
                                                  </>
                                                ) : null
                                              )}
                                            </>
                                          ) : null
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          )}

                          {/* 大运/流年神煞摘要 + 联动规则 */}
                          {activeDayun && selectedLiunian && activeLiunian && (() => {
                            const dayunStars = shenshaTagItems(activeDayun.items, 6);
                            const liunianStars = shenshaTagItems(activeLiunian.items, 8);
                            const rules = transitLinkRules(activeDayun, activeLiunian);
                            const activeDayunBase = activeDayunIndex >= 0 ? props.daYun[activeDayunIndex] : undefined;
                            return (
                              <div className="bazi-transit-summary space-y-3">
                                <div className="rounded-2xl p-4 sm:p-4" style={{ background: transitCardBg, border: `1px solid ${transitCardBorder}` }}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <h4 className="bazi-transit-card-title font-bold" style={{ color: transitAccentColor, fontFamily: "'Noto Serif TC', serif" }}>
                                        {isZh ? trd('当前大运神煞') : 'Current Decade Stars'}
                                      </h4>
                                      <p className="bazi-transit-meta mt-1" style={{ color: transitLabelColor }}>
                                        {activeDayun.startYear}{activeDayunBase ? ` · ${translateAge(activeDayunBase.ageRange, activeDayunBase.ageRange_en)}` : ''}
                                      </p>
                                      {renderShenshaTags(dayunStars, isZh ? trd('当前大运') : 'Current Decade')}
                                    </div>
                                    <div className="bazi-transit-pillar flex-shrink-0 font-bold leading-none text-right" style={{ fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>
                                      <span style={{ color: getStemDisplayColor(activeDayun.stem) }}>{tStem(activeDayun.stem)}</span>
                                      <span style={{ color: getBranchDisplayColor(activeDayun.branch) }}>{tBranch(activeDayun.branch)}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="rounded-2xl p-4 sm:p-4" style={{ background: transitCardBg, border: `1px solid ${transitCardBorder}` }}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <h4 className="bazi-transit-card-title font-bold" style={{ color: transitAccentColor, fontFamily: "'Noto Serif TC', serif" }}>
                                        {isZh ? trd('当前流年神煞') : 'Current Annual Stars'}
                                      </h4>
                                      <p className="bazi-transit-meta mt-1" style={{ color: transitLabelColor }}>
                                        {activeLiunian.year ?? selectedLiunianYear}
                                      </p>
                                      {renderShenshaTags(liunianStars, isZh ? trd('当前流年') : 'Current Annual')}
                                    </div>
                                    <div className="bazi-transit-pillar flex-shrink-0 font-bold leading-tight text-right" style={{ fontFamily: isZh ? "'Noto Serif TC', serif" : 'inherit' }}>
                                      <span style={{ color: getStemDisplayColor(activeLiunian.stem) }}>{tStem(activeLiunian.stem)}</span>
                                      <span style={{ color: getBranchDisplayColor(activeLiunian.branch) }}>{tBranch(activeLiunian.branch)}</span>
                                    </div>
                                  </div>
                                </div>

                                <div>
                                  <button
                                    type="button"
                                    className="w-full flex items-center gap-2 mb-3 text-left active:opacity-80 transition-opacity"
                                    onClick={() => setShowTransitLinkRules(prev => !prev)}
                                    aria-expanded={showTransitLinkRules}
                                  >
                                    <i className="ri-link-m bazi-transit-section-icon flex-shrink-0" style={{ color: transitAccentColor }}></i>
                                    <h4 className="bazi-transit-section-title font-bold flex-1 min-w-0" style={{ color: transitValueColor, fontFamily: "'Noto Serif TC', serif" }}>
                                      {isZh ? trd('大运流年联动规则') : 'Decade And Annual Rules'}
                                    </h4>
                                    <i
                                      className={`${showTransitLinkRules ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} bazi-transit-section-icon flex-shrink-0`}
                                      style={{ color: transitAccentColor }}
                                    ></i>
                                  </button>
                                  {showTransitLinkRules && (
                                    <div className="space-y-2">
                                      {rules.map((rule, idx) => (
                                        <div
                                          key={idx}
                                          className="rounded-xl px-4 py-3 flex items-start gap-3"
                                          style={{ background: transitCardBg, border: `1px solid ${transitCardBorder}` }}
                                        >
                                          <span className="bazi-transit-rule-index font-bold" style={{ color: isDark ? goldColor : '#4f5358' }}>{idx + 1}</span>
                                          <p className="bazi-transit-rule-text" style={{ color: transitLabelColor }}>{rule}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ProfessionalFourPillarGrid>

              {/* 日主 · 格局 · 喜用神 综合面板（服务端 rizhu/geju/xiyong 驱动，4 格只读 + 喜忌 chips） */}
              {(props.geju || props.xiyong) && (() => {
                const bodyStrength = props.xiyong?.bodyStrength;
                const bodyStrengthValue = props.xiyong?.wangShuaiLabel
                  || (bodyStrength === 'strong' ? trd('偏旺') : bodyStrength === 'weak' ? trd('偏弱') : trd('中和'));
                const bodyStrengthColor = bodyStrength === 'strong' ? '#D14B45' : bodyStrength === 'weak' ? '#7A9ECF' : '#C8A96E';
                const yinYangColor = yinYangRef.includes('阳') ? '#D14B45' : yinYangRef.includes('阴') ? '#7A9ECF' : '#C8A96E';
                const elIcon = (el: string) => ELEMENT_ICONS[el as Element] || '';
                const renderChips = (items: Array<{ element: string; color?: string }>) => items.length > 0 ? items.map((item, idx) => {
                  const color = item.color || ELEMENT_COLORS[item.element as Element] || valueColor;
                  const icon = elIcon(item.element);
                  return (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: `${color}18`, border: `1px solid ${color}40` }}>
                      {icon ? <i className={`${icon} text-[10px]`} style={{ color }} /> : null}
                      <span className="text-[10px] font-bold" style={{ color }}>{item.element}</span>
                    </span>
                  );
                }) : null;
                return (
                  <div className="rounded-2xl overflow-hidden border" style={{ background: cardBg, borderColor: cardBorder }}>
                    <div className="px-3 py-2.5 flex items-center gap-2" style={{ borderBottom: `1px solid ${rowBorderLight}` }}>
                      <div className="w-1 h-3 rounded-full" style={{ background: goldColor }} />
                      <span className="text-xs font-bold" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>
                        {isZh ? trd('日主 · 格局 · 喜用神（简化）') : lang === 'en' ? 'Day Master · Pattern · Lucky Elements' : lang === 'ko' ? '일주 · 격국 · 희용신' : '日主 · 格局 · 用神'}
                      </span>
                      <span
                        className="ml-auto flex items-center gap-1 text-[0.55rem] font-bold px-2 py-1 rounded-full"
                        style={{ background: 'rgba(232,160,32,0.12)', color: '#C8960A', border: '1px solid rgba(232,160,32,0.3)' }}
                      >
                        <i className="ri-lightbulb-line text-xs" />
                        {isZh ? (
                          <>
                            {trd('深度解读详见')}
                            <span style={{ color: isDark ? '#D98A2B' : '#B45309' }}>{trd('【AI命理报告】')}</span>
                          </>
                        ) : 'Insight'}
                      </span>
                    </div>

                    <div className="grid grid-cols-4 border-b" style={{ borderColor: rowBorderLight }}>
                      {[
                        { label: trd('日主属性'), value: `${dayStem}${dayStemEl}`, color: getStemColor(dayStem) },
                        { label: trd('阴阳参考'), value: yinYangRef, color: yinYangColor },
                        { label: trd('旺衰参考'), value: bodyStrengthValue, color: bodyStrengthColor },
                        { label: trd('格局参考'), value: props.geju?.name || '——', color: '#C8A96E' },
                      ].map((item, idx) => (
                        <div
                          key={idx}
                          className="flex flex-col items-center justify-center py-2 px-1 gap-0.5"
                          style={{ borderRight: idx < 3 ? `1px solid ${rowBorderLight}` : 'none' }}
                        >
                          <span className="text-[9px]" style={{ color: labelColor }}>{item.label}</span>
                          <span className="text-[11px] font-bold" style={{ color: item.color, fontFamily: isZh ? "'Noto Serif TC', serif" : undefined }}>
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    {props.xiyong && (<>
                      <div className="px-3 py-2.5 border-b" style={{ borderColor: rowBorderLight }}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="text-[10px] font-bold" style={{ color: accentColor }}>
                            {isZh ? trd('喜用神') : lang === 'en' ? 'Lucky Elements' : lang === 'ko' ? '희용신' : '喜用神'}
                          </span>
                          <span className="text-[9px]" style={{ color: labelColor }}>
                            · {isZh ? trd('宜接触、助力') : lang === 'en' ? 'Beneficial' : lang === 'ko' ? '도움' : '好運の要素'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {renderChips(props.xiyong.xi) ?? <span className="text-[10px]" style={{ color: labelColor }}>{trd('平衡')}</span>}
                        </div>
                      </div>
                      <div className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="text-[10px] font-bold" style={{ color: '#D14B45' }}>
                            {isZh ? trd('忌神') : lang === 'en' ? 'Unfavorable Elements' : lang === 'ko' ? '기신' : '忌神'}
                          </span>
                          <span className="text-[9px]" style={{ color: labelColor }}>
                            · {isZh ? trd('宜回避、减少') : lang === 'en' ? 'Avoid' : lang === 'ko' ? '회피' : '避けるべき要素'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {renderChips(props.xiyong.ji) ?? <span className="text-[10px]" style={{ color: labelColor }}>{trd('不明显')}</span>}
                        </div>
                      </div>
                    </>)}
                  </div>
                );
              })()}

              {props.fourPillars && (
                <CompactWuxingPanel
                  fourPillars={props.fourPillars}
                  isDark={isDark}
                  isZh={isZh}
                  rowBorder={rowBorder}
                  labelColor={labelColor}
                  valueColor={valueColor}
                  accentColor={accentColor}
                  goldColor={goldColor}
                />
              )}

              {/* 人元司令分野 + 月令详解 + 节气卡片 */}
              <div className="rounded-2xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                <div
                  role="button"
                  tabIndex={0}
                  className="px-2 md:px-3 py-2.5 flex items-center justify-between cursor-pointer active:opacity-80 transition-opacity"
                  onClick={() => setShowYueling(v => !v)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setShowYueling(v => !v); }}
                  style={{ background: showYueling ? (isDark ? 'rgba(183,154,197,0.06)' : 'rgba(232,160,32,0.04)') : undefined }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-3 rounded-full" style={{ background: goldColor }}></div>
                    <span className="text-xs font-bold" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>
                      {yuelingData
                        ? trd(`${yuelingData.branchName} · ${yuelingData.activeDesc}第${yuelingData.activeDayNumber}天`)
                        : (isZh ? trd('月令五行用事') : lang === 'en' ? 'Month Branch Ruler' : lang === 'ko' ? '월령오행용사' : '月令五行用事')}
                    </span>
                    {yuelingData && (
                      <span
                        className="text-[0.5rem] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
                        style={{
                          background: yuelingData.qiPhase === '节'
                            ? (isDark ? 'rgba(123,198,126,0.2)' : 'rgba(123,198,126,0.15)')
                            : (isDark ? 'rgba(232,160,32,0.2)' : 'rgba(232,160,32,0.15)'),
                          color: yuelingData.qiPhase === '节' ? '#7BC67E' : goldColor,
                          border: `1px solid ${yuelingData.qiPhase === '节' ? '#7BC67E' : goldColor}40`,
                        }}
                      >
                        {yuelingData.qiPhase}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {yuelingData && (
                      <div className="flex items-center gap-1">
                        {yuelingData.items.map((it, i) => (
                          <div
                            key={i}
                            className="rounded-full"
                            style={{
                              width: it.isActive ? 8 : 5,
                              height: it.isActive ? 8 : 5,
                              background: it.color,
                              opacity: it.isActive ? 1 : 0.35,
                            }}
                          />
                        ))}
                      </div>
                    )}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${showYueling ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} style={{ color: labelColor }}></i>
                    </div>
                  </div>
                </div>

                {showYueling && yuelingData && (
                  <div className="border-t px-2 md:px-3 py-3 space-y-3" style={{ borderColor: rowBorderLight }}>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold" style={{ color: accentColor }}>
                          {isZh ? trd('人元司令分野') : lang === 'en' ? 'Hidden Stem Phases' : lang === 'ko' ? '인원사령분야' : '人元司令分野'}
                        </span>
                        <span className="text-[9px]" style={{ color: labelColor }}>
                          {isZh ? trd(`共${yuelingData.totalDays}天`) : lang === 'en' ? `${yuelingData.totalDays} days` : lang === 'ko' ? `총 ${yuelingData.totalDays}일` : `計${yuelingData.totalDays}日`}
                        </span>
                      </div>
                      <div className="flex h-3 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(107,78,113,0.3)' : 'rgba(232,160,32,0.12)' }}>
                        {yuelingData.items.map((it, i) => (
                          <div
                            key={i}
                            className="h-full relative"
                            style={{
                              width: `${it.percent}%`,
                              background: it.isActive ? it.color : `${it.color}60`,
                              minWidth: 2,
                            }}
                          >
                            {it.isActive && (
                              <div className="absolute inset-0 animate-pulse" style={{ background: 'rgba(255,255,255,0.2)' }} />
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between gap-1 flex-wrap">
                        {yuelingData.items.map((it, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: it.color }} />
                            <span className="text-[9px]" style={{ color: it.isActive ? valueColor : labelColor, fontWeight: it.isActive ? 700 : 400 }}>
                              {it.stem}{it.element} {it.days}{isZh ? trd('天') : lang === 'en' ? 'd' : lang === 'ko' ? '일' : '日'}
                            </span>
                            {it.isActive && (
                              <span className="text-[0.5rem] px-1 py-0.5 rounded-full font-bold flex-shrink-0" style={{ background: `${it.color}25`, color: it.color, border: `1px solid ${it.color}50` }}>
                                {isZh ? trd('当前') : lang === 'en' ? 'Now' : lang === 'ko' ? '현재' : '現在'}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: rowBorderLight, background: isDark ? 'rgba(107,78,113,0.12)' : 'rgba(245,245,247,0.6)' }}>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                        onClick={() => setExpandedYueling(v => !v)}
                      >
                        <span className="text-[10px] font-bold" style={{ color: accentColor }}>
                          {trd(yuelingData.branchName)}{isZh ? trd('详解') : lang === 'en' ? ' Details' : lang === 'ko' ? ' 상세' : '詳解'}
                        </span>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${expandedYueling ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`} style={{ color: labelColor }}></i>
                        </div>
                      </button>
                      {expandedYueling && (
                        <div className="px-3 pb-3 pt-1 border-t space-y-2" style={{ borderColor: rowBorderLight }}>
                          <p className="text-[0.65rem] leading-relaxed" style={{ color: valueColor }}>
                            {trd(yuelingData.detail.title)}
                          </p>
                          <div className="space-y-1.5">
                            {[
                              { icon: 'ri-user-line', label: isZh ? trd('性格') : lang === 'en' ? 'Personality' : lang === 'ko' ? '성격' : '性格', text: trd(yuelingData.detail.personality) },
                              { icon: 'ri-briefcase-line', label: isZh ? trd('事业') : lang === 'en' ? 'Career' : lang === 'ko' ? '사업' : '事業', text: trd(yuelingData.detail.career) },
                              { icon: 'ri-heart-line', label: isZh ? trd('感情') : lang === 'en' ? 'Relationships' : lang === 'ko' ? '감정' : '感情', text: trd(yuelingData.detail.emotion) },
                              { icon: 'ri-health-book-line', label: isZh ? trd('健康') : lang === 'en' ? 'Health' : lang === 'ko' ? '건강' : '健康', text: trd(yuelingData.detail.health) },
                              { icon: 'ri-lightbulb-line', label: isZh ? trd('建议') : lang === 'en' ? 'Advice' : lang === 'ko' ? '조언' : '提案', text: trd(yuelingData.detail.advice) },
                            ].map((row, i) => (
                              <div key={i} className="flex items-start gap-1.5">
                                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <i className={`${row.icon} text-[10px]`} style={{ color: accentColor }}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-[0.6rem] font-bold" style={{ color: accentColor }}>{row.label}：</span>
                                  <span className="text-[0.6rem] leading-relaxed" style={{ color: isDark ? 'rgba(232,224,232,0.8)' : '#4A4A4A' }}>{row.text}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 二十四节气 */}
                <div className="px-2 md:px-3 py-3" style={{ borderTop: showYueling && yuelingData ? `1px solid ${rowBorderLight}` : undefined }}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-3 rounded-full" style={{ background: goldColor }}></div>
                    <span className="text-xs font-bold" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>
                      {isZh ? trd('二十四节气') : lang === 'en' ? '24 Solar Terms' : lang === 'ko' ? '이십사절기' : '二十四節気'}
                    </span>
                    <span className="text-[9px]" style={{ color: labelColor }}>· {isZh ? trd('点击节气查看详情') : lang === 'en' ? 'Tap to view' : lang === 'ko' ? '탭하여 상세보기' : 'タップで詳細'}</span>
                  </div>
                  <div className="grid grid-cols-6 gap-1.5">
                    {jieQiList.map((jqIdx) => {
                      const jqName = t(`bazi.jieQi.${jqIdx}`) as string;
                      const branch = JIEQI_BRANCH_MAP[jqIdx];
                      const branchName = isZh ? trd(`${branch}月`) : `${branch}月`;
                      const isMing = mingBranch === branch;
                      return (
                        <button
                          key={jqIdx}
                          type="button"
                          className="flex flex-col items-center py-2 px-1 rounded-xl border active:opacity-80 transition-opacity"
                          onClick={() => { setSelectedJieQi(jqIdx); setShowJieQiModal(true); }}
                          style={{
                            borderColor: isMing ? `${goldColor}60` : rowBorderLight,
                            background: isMing ? (isDark ? `${goldColor}12` : `${goldColor}08`) : (isDark ? 'rgba(107,78,113,0.08)' : 'rgba(245,245,247,0.4)'),
                          }}
                        >
                          <span className="text-[10px] font-bold mb-0.5" style={{ color: isMing ? goldColor : valueColor, fontFamily: isZh ? "'Noto Serif TC', serif" : "inherit" }}>{jqName}</span>
                          <span className="text-[0.5rem]" style={{ color: labelColor }}>{branchName}</span>
                          {isMing && (
                            <span className="text-[0.4rem] px-1 py-0.5 rounded-full mt-0.5" style={{ background: `${goldColor}25`, color: goldColor }}>{trd('命')}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 底部说明：专业排盘说明 + 免责声明 */}
              <div className="rounded-2xl overflow-hidden" style={{ background: isDark ? 'rgba(183,154,197,0.08)' : 'rgba(232,160,32,0.06)', border: `1px solid ${isDark ? 'rgba(183,154,197,0.2)' : 'rgba(232,160,32,0.18)'}` }}>
                <div className="px-2 md:px-3 py-2 flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: accentColor }}>
                    <i className="ri-information-line text-white text-[10px]"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold mb-0.5" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>{t('professionalPaipan.noteTitle')}</p>
                    <p className="text-[9px] leading-relaxed" style={{ color: labelColor }}>{t('professionalPaipan.ruleDesc')}</p>
                  </div>
                </div>
                <div className="px-2 md:px-3 py-2 border-t flex items-start gap-1.5" style={{ borderColor: isDark ? 'rgba(232,160,32,0.2)' : 'rgba(232,160,32,0.15)', background: isDark ? 'rgba(232,160,32,0.06)' : 'rgba(232,160,32,0.04)' }}>
                  <i className="ri-alarm-warning-line text-[10px] mt-0.5 flex-shrink-0" style={{ color: '#E8A020' }}></i>
                  <p className="text-[9px] leading-relaxed" style={{ color: '#E8A020' }}>{t('paipanRisk.notice')}</p>
                </div>
              </div>
            </div>
          )}
      </div>
      <ShenshaDetailModal selected={selectedShensha} onClose={() => setSelectedShensha(null)} isDark={isDark} />
      <BaziInsightModal
        type={activeModal || 'rizhu'}
        isOpen={activeModal !== null}
        onClose={() => setActiveModal(null)}
        isDark={isDark}
        fourPillars={props.fourPillars}
        geju={props.geju}
        xiyong={props.xiyong}
        yinYangRef={calcYinYangRef(props.fourPillars, props.gender)}
        gender={props.gender}
        dayStem={mainPillars[2]?.tianGan}
        rizhu={props.rizhu}
      />
      <JieQiModal
        jieQiIdx={selectedJieQi}
        dayStem={mainPillars[2]?.tianGan || ''}
        isOpen={showJieQiModal}
        onClose={() => setShowJieQiModal(false)}
        isDark={isDark}
      />
    </div>
  );
}
